import { DiscountCodeInput } from "@/components/discount-code-input";
import { discountError, type DiscountSelection } from "@/lib/discount.types";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BedDouble,
  Building2,
  Car,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  CircleDollarSign,
  ExternalLink,
  FileText,
  Heart,
  House,
  Images,
  KeyRound,
  Layers3,
  Mail,
  MapPin,
  Phone,
  Ruler,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Trees,
  UserRoundPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { PropertyPortalStrip } from "@/components/property-portal-strip";
import { LiestalPropertyFlyer } from "@/components/liestal-property-flyer";
import { OffMarketBuyerInquiry } from "@/components/property-inquiry-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FORM_TEXT_MAX_LENGTH,
  FORM_TEXT_MIN_LENGTH,
  FormTextCounter,
} from "@/components/form-text-counter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  PROPERTY_FORM_KINDS,
  PROPERTY_SUBTYPES,
  propertyListings,
  type ListingMode,
  type PropertyAmenity,
  type PropertyCondition,
  type PropertyKind,
  type PropertyListing,
} from "@/data/property-listings";
import { useLanguage, type LanguageCode } from "@/lib/i18n";
import { useServerFn } from "@tanstack/react-start";
import { submitForm } from "@/lib/forms.functions";
import { uploadFormImages } from "@/lib/uploads.functions";
import { filesToPayload } from "@/lib/file-upload";
import { getPublicListings, getPublicProviders } from "@/lib/public-content.functions";
import type { ProviderRow } from "@/lib/public-content.types";
import { emailMessages } from "@/lib/email/messages";
import { billingPlanName, fallbackBillingPlans } from "@/lib/billing.catalog";
import { formatBillingPrice } from "@/lib/billing.types";
import agentPortrait from "@/assets/maklerprofil-portrait.webp";

export const Route = createFileRoute("/immobilien")({
  staticData: { sitemap: true, toasts: true },
  validateSearch: (
    search: Record<string, unknown>,
  ): { paket?: "property_14_days" | "property_30_days" } =>
    search.paket === "property_14_days" || search.paket === "property_30_days"
      ? { paket: search.paket }
      : {},
  head: () => ({
    meta: [
      { title: "Immobilien in der Schweiz. Begner" },
      {
        name: "description",
        content:
          "Wohnungen und Häuser in der neuen Begner Immobilienansicht entdecken und vergleichen.",
      },
      { property: "og:title", content: "Immobilien entdecken. Begner" },
      {
        property: "og:description",
        content: "Ausgewählte Wohnungen und Häuser aus Ihrer Region.",
      },
    ],
  }),
  component: ImmobilienPage,
});

type ModeFilter = "all" | ListingMode;
type KindFilter = "all" | PropertyKind;
type SortOption = "newest" | "price-asc" | "price-desc" | "area-desc";

const MAX_PROPERTY_PHOTOS = 7;

const demoAgent: ProviderRow = {
  id: "__demo-agent",
  created_at: "2026-08-31T00:00:00.000Z",
  type: "immobilienmakler",
  name: "Sofia Keller",
  category: "Keller Immobilien · Verkauf & Bewertung",
  description:
    "Persönliche Immobilienberatung für Verkauf, Bewertung und Vermietung in der Region Basel – klar, verbindlich und mit Blick fürs Detail.",
  location: "Basel & Nordwestschweiz",
  canton: "BS",
  services: ["Immobilienverkauf", "Marktwertanalyse", "Vermietung"],
  phone: "+41 61 000 00 00",
  email: "sofia.keller@example.com",
  website: "https://example.com",
  image_url: agentPortrait,
  verified: true,
  rating: 4.9,
  review_count: 24,
  status: "published",
};

const agentProfileCopy = {
  de: {
    eyebrow: "Persönlich für Sie da",
    title: "Immobilienkompetenz mit einem Gesicht",
    text: "Lernen Sie geprüfte Maklerinnen und Makler kennen, die Ihre Immobilie persönlich, lokal und professionell begleiten.",
    agent: "Immobilienprofil",
    verified: "Geprüftes Profil",
    sample: "Beispielprofil",
    reviews: "Bewertungen",
    services: "Schwerpunkte",
    contact: "Direktkontakt",
    email: "E-Mail schreiben",
    website: "Website besuchen",
  },
  fr: {
    eyebrow: "À vos côtés",
    title: "L’expertise immobilière, incarnée",
    text: "Découvrez des professionnelles et professionnels vérifiés qui vous accompagnent personnellement et localement.",
    agent: "Profil immobilier",
    verified: "Profil vérifié",
    sample: "Profil d’exemple",
    reviews: "avis",
    services: "Spécialités",
    contact: "Contact direct",
    email: "Écrire un e-mail",
    website: "Visiter le site",
  },
  it: {
    eyebrow: "Al vostro fianco",
    title: "Competenza immobiliare con un volto",
    text: "Scoprite professionisti verificati che vi accompagnano in modo personale, locale e competente.",
    agent: "Profilo immobiliare",
    verified: "Profilo verificato",
    sample: "Profilo di esempio",
    reviews: "recensioni",
    services: "Specializzazioni",
    contact: "Contatto diretto",
    email: "Scrivi un’e-mail",
    website: "Visita il sito",
  },
  en: {
    eyebrow: "Here for you personally",
    title: "Real-estate expertise with a face",
    text: "Meet verified real-estate professionals who support you personally, locally and professionally.",
    agent: "Real-estate profile",
    verified: "Verified profile",
    sample: "Sample profile",
    reviews: "reviews",
    services: "Specialisms",
    contact: "Direct contact",
    email: "Send email",
    website: "Visit website",
  },
} satisfies Record<LanguageCode, Record<string, string>>;

const realEstateCopy = {
  de: {
    locale: "de-CH",
    heroTitle: "Räume für das,",
    heroHighlight: "was als Nächstes kommt.",
    heroText:
      "Entdecken Sie ausgewählte Wohnungen und Häuser aus Ihrer Region – persönlich inseriert, klar präsentiert und einfach zu vergleichen.",
    heroTrust: ["Für private Inserate", "Transparent & lokal", "Kostenlos suchen"],
    searchLabel: "Immobilien durchsuchen",
    locationPlaceholder: "Ort, Region oder PLZ",
    searchButton: "Objekte finden",
    featured: "Neu entdeckt",
    featuredHint: "Familienhaus in Reinach BL",
    featuredPrice: "CHF 1’480’000",
    mode: { all: "Alle", buy: "Kaufen", rent: "Mieten" },
    propertyTypes: {
      apartment: "Wohnung",
      house: "Haus",
      apartmentBuilding: "Mehrfamilienhaus",
      land: "Grundstück",
      hospitality: "Gastronomie",
      commercial: "Gewerbe/Industrie",
      parking: "Parkplatz",
      chalet: "Chalet",
    },
    allTypes: "Alle Objekttypen",
    anyRooms: "Beliebig viele Zimmer",
    roomsPlus: "Zimmer",
    anyBudget: "Jedes Budget",
    chooseModeForBudget: "Kaufen oder Mieten wählen",
    upTo: "Bis",
    discoverEyebrow: "Aus der Region",
    discoverTitle: "Immobilien, die sich richtig anfühlen",
    discoverText:
      "Suchen Sie gezielt oder lassen Sie sich von ausgewählten Objekten aus der Nordwestschweiz inspirieren.",
    demoNote: "Vorschau mit Beispiel-Inseraten",
    resultOne: "Objekt",
    resultMany: "Objekte",
    filters: "Filter",
    filterHint: "Grenzen Sie die Auswahl nach Ihren Wünschen ein.",
    offerType: "Angebot",
    objectType: "Objekttyp",
    rooms: "Zimmer",
    maxPrice: "Maximalpreis",
    features: "Ausstattung",
    reset: "Alles zurücksetzen",
    mobileFilters: "Filter anzeigen",
    showResults: "Ergebnisse anzeigen",
    sort: "Sortieren",
    sortLabels: {
      newest: "Neueste zuerst",
      "price-asc": "Preis aufsteigend",
      "price-desc": "Preis absteigend",
      "area-desc": "Grösste Fläche",
    },
    amenities: {
      balcony: "Balkon / Terrasse",
      garden: "Garten",
      parking: "Parkplatz / Garage",
      lift: "Lift",
      pets: "Haustiere erlaubt",
      accessible: "Rollstuhlgängig",
      dishwasher: "Geschirrspüler",
      washingMachine: "Eigener Waschturm",
    },
    conditionLabels: {
      new: "Neubau / Erstbezug",
      renovated: "Renoviert",
      good: "Guter Zustand",
      needsRenovation: "Renovationsbedürftig",
    },
    filterFor: "Filter für",
    noResultsTitle: "Noch nicht das Richtige dabei",
    noResultsText:
      "Ändern Sie einen Filter oder setzen Sie die Auswahl zurück, um wieder mehr Objekte zu sehen.",
    clearFilters: "Filter zurücksetzen",
    demoBadge: "Beispiel",
    draftBadge: "Ihr Entwurf",
    privateBadge: "Inserat-Vorschau",
    monthly: "/ Monat",
    additionalCosts: "Nebenkosten",
    available: "Verfügbar nach Vereinbarung",
    availableFrom: "Verfügbar ab",
    bathrooms: "Badezimmer",
    constructionYear: "Baujahr",
    condition: "Zustand",
    floor: "Etage",
    details: "Details ansehen",
    save: "Objekt merken",
    unsave: "Aus Merkliste entfernen",
    detailEyebrow: "Immobilien-Vorschau",
    detailDescription: "Dieses Inserat ist ein fiktives Beispiel für die neue Immobilienansicht.",
    contact: "Interesse vormerken",
    contactToast: "Interesse vorgemerkt – die Kontaktfunktion folgt mit dem Livegang.",
    close: "Schliessen",
    ctaEyebrow: "Platz für Ihr Inserat",
    ctaTitle: "Ihre Immobilie. Schön präsentiert.",
    ctaText:
      "Erstellen Sie in wenigen Schritten einen Inserat-Entwurf und sehen Sie direkt, wie Ihr Objekt auf Begner wirken könnte.",
    ctaButton: "Immobilie inserieren",
    ctaSecondary: "Inserate entdecken",
    agentRegister: "Als Immobilienmakler registrieren",
    pricingLink: "Inseratpreise ansehen",
    formTitle: "Inserat als Entwurf anlegen",
    formDescription:
      "Die Angaben werden nur für diese lokale Vorschau verwendet und noch nicht veröffentlicht.",
    formRequiredHint: "* Pflichtfelder",
    formOverviewTitle: "Ihr Inserat im Überblick",
    formOverviewText:
      "Füllen Sie die Bereiche der Reihe nach aus. Ihre Angaben bleiben beim Wechsel zwischen den Abschnitten erhalten.",
    formPrivacyHint: "Ihre Kontaktdaten werden nicht im Inserat veröffentlicht.",
    formSectionOffer: "Angebot & Objekt",
    formSectionOfferHint: "Legen Sie zuerst fest, was Sie anbieten.",
    formSectionLocation: "Adresse",
    formSectionLocationHint: "Die genaue Adresse bleibt auf Wunsch verborgen.",
    formSectionDetails: "Eckdaten",
    formSectionDetailsHint: "Die wichtigsten Fakten für Interessierte.",
    formSectionPrice: "Preis & Verfügbarkeit",
    formSectionPriceHint: "Transparente Angaben erleichtern den Vergleich.",
    formSectionPresentation: "Beschreibung & Ausstattung",
    formSectionPresentationHint: "Zeigen Sie, was Ihre Immobilie besonders macht.",
    formSectionMedia: "Bilder & Grundriss",
    formSectionContact: "Kontakt",
    formSectionContactHint: "So können wir Rückfragen zu Ihrem Inserat klären.",
    formObjectTitle: "Titel des Inserats",
    formTitlePlaceholder: "z. B. Ruhige Wohnung mit Balkon",
    formTitleHint: "Kurz und konkret – die wichtigsten Vorzüge zuerst.",
    formLocation: "Ort",
    formLocationPlaceholder: "z. B. Muttenz",
    formStreet: "Strasse und Hausnummer",
    formStreetPlaceholder: "z. B. Hauptstrasse 12",
    formPostalCode: "PLZ",
    formPublishAddress: "Genaue Adresse im Inserat anzeigen",
    formPublishAddressHint: "Ohne Auswahl werden nur PLZ und Ort veröffentlicht.",
    formPrice: "Mietzins / Kaufpreis in CHF",
    formAdditionalCosts: "Nebenkosten in CHF",
    formAvailability: "Verfügbar ab",
    formRooms: "Zimmer",
    formArea: "Wohnfläche in m²",
    formFloor: "Etage",
    formFloorPlaceholder: "z. B. 2. OG",
    formBathrooms: "Badezimmer / WC",
    formConstructionYear: "Baujahr",
    formCondition: "Zustand",
    formMode: "Angebotsart",
    formKind: "Objekttyp",
    formSubkind: "Objektart",
    formSubkindPlaceholder: "Bitte auswählen",
    formDescriptionLabel: "Objektbeschreibung",
    formDescriptionMinimum: "Mindestens 40 Zeichen",
    formDescriptionPlaceholder:
      "Beschreiben Sie Raumaufteilung, Küche und Bad, Besonderheiten sowie die Umgebung.",
    formAmenities: "Ausstattung und Merkmale",
    formAmenitiesHint: "Mehrfachauswahl möglich",
    formPhotos: "Fotos (max. 7)",
    formPhotosLimitError: "Bitte wählen Sie höchstens 7 Bilder aus.",
    formPhotosRequiredError: "Bitte laden Sie mindestens ein Bild für das Inserat hoch.",
    formPhotosHint: "JPG, PNG oder WebP. Das erste Bild wird als Titelbild verwendet.",
    formFloorPlan: "Grundriss oder Dokumentation",
    formMediaHint:
      "Optional für die Vorschau. Verwenden Sie nur Dateien, an denen Sie die Rechte besitzen.",
    formContactName: "Kontaktperson",
    formContactEmail: "E-Mail-Adresse",
    formContactPhone: "Telefonnummer",
    formCancel: "Abbrechen",
    formSubmit: "Entwurf anzeigen",
    formSubmitHint: "Vor dem Absenden können Sie alle Angaben nochmals prüfen.",
    formErrorRequired: "Bitte füllen Sie dieses Pflichtfeld aus.",
    formErrorPostalCode: "Bitte geben Sie eine vierstellige Schweizer PLZ ein.",
    formErrorEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    formErrorPositiveNumber: "Bitte geben Sie einen Wert grösser als 0 ein.",
    formErrorNonNegativeNumber: "Bitte geben Sie 0 oder einen höheren Wert ein.",
    formErrorYear: "Bitte geben Sie ein Jahr zwischen 1500 und 2100 ein.",
    formErrorSummary: "Bitte prüfen Sie die markierten Felder.",
    formSuccess: "Ihr Inserat-Entwurf wurde der Vorschau hinzugefügt.",
    draftDescription: "Ihr persönlicher Inserat-Entwurf in der aktuellen Vorschau.",
  },
  fr: {
    locale: "fr-CH",
    heroTitle: "Des espaces pour",
    heroHighlight: "la suite de votre histoire.",
    heroText:
      "Découvrez des appartements et maisons de votre région, présentés avec clarté et faciles à comparer.",
    heroTrust: ["Pour les annonces privées", "Transparent et local", "Recherche gratuite"],
    searchLabel: "Rechercher un bien",
    locationPlaceholder: "Localité, région ou NPA",
    searchButton: "Trouver un bien",
    featured: "À découvrir",
    featuredHint: "Maison familiale à Reinach BL",
    featuredPrice: "CHF 1’480’000",
    mode: { all: "Tous", buy: "Acheter", rent: "Louer" },
    propertyTypes: {
      apartment: "Appartement",
      house: "Maison",
      apartmentBuilding: "Immeuble collectif",
      land: "Terrain",
      hospitality: "Gastronomie",
      commercial: "Commerce/industrie",
      parking: "Place de stationnement",
      chalet: "Chalet",
    },
    allTypes: "Tous les types",
    anyRooms: "Nombre de pièces indifférent",
    roomsPlus: "pièces",
    anyBudget: "Tous les budgets",
    chooseModeForBudget: "Choisir achat ou location",
    upTo: "Jusqu'à",
    discoverEyebrow: "Dans votre région",
    discoverTitle: "Des biens dans lesquels on se projette",
    discoverText:
      "Recherchez précisément ou laissez-vous inspirer par une sélection de biens du nord-ouest de la Suisse.",
    demoNote: "Aperçu avec annonces d'exemple",
    resultOne: "bien",
    resultMany: "biens",
    filters: "Filtres",
    filterHint: "Affinez la sélection selon vos envies.",
    offerType: "Offre",
    objectType: "Type de bien",
    rooms: "Pièces",
    maxPrice: "Prix maximum",
    features: "Équipement",
    reset: "Tout réinitialiser",
    mobileFilters: "Afficher les filtres",
    showResults: "Afficher les résultats",
    sort: "Trier",
    sortLabels: {
      newest: "Plus récents",
      "price-asc": "Prix croissant",
      "price-desc": "Prix décroissant",
      "area-desc": "Plus grande surface",
    },
    amenities: {
      balcony: "Balcon / terrasse",
      garden: "Jardin",
      parking: "Parking / garage",
      lift: "Ascenseur",
      pets: "Animaux acceptés",
      accessible: "Accessible en fauteuil roulant",
      dishwasher: "Lave-vaisselle",
      washingMachine: "Colonne de lavage privée",
    },
    conditionLabels: {
      new: "Neuf / première occupation",
      renovated: "Rénové",
      good: "Bon état",
      needsRenovation: "À rénover",
    },
    filterFor: "Filtre",
    noResultsTitle: "Le bien idéal n'est pas encore là",
    noResultsText: "Modifiez un filtre ou réinitialisez la sélection pour voir plus de biens.",
    clearFilters: "Réinitialiser les filtres",
    demoBadge: "Exemple",
    draftBadge: "Votre brouillon",
    privateBadge: "Aperçu de l'annonce",
    monthly: "/ mois",
    additionalCosts: "Charges",
    available: "Disponible à convenir",
    availableFrom: "Disponible dès",
    bathrooms: "Salles de bains",
    constructionYear: "Année de construction",
    condition: "État",
    floor: "Étage",
    details: "Voir les détails",
    save: "Enregistrer le bien",
    unsave: "Retirer des favoris",
    detailEyebrow: "Aperçu immobilier",
    detailDescription: "Cette annonce fictive illustre la nouvelle vue immobilière.",
    contact: "Signaler mon intérêt",
    contactToast: "Intérêt enregistré – le contact sera activé lors du lancement.",
    close: "Fermer",
    ctaEyebrow: "Votre annonce a sa place ici",
    ctaTitle: "Votre bien. Joliment présenté.",
    ctaText:
      "Créez un brouillon en quelques étapes et voyez immédiatement comment votre bien pourrait apparaître sur Begner.",
    ctaButton: "Publier un bien",
    ctaSecondary: "Découvrir les annonces",
    agentRegister: "S'inscrire comme agent immobilier",
    pricingLink: "Voir les prix des annonces",
    formTitle: "Créer un brouillon d'annonce",
    formDescription: "Les données servent uniquement à cet aperçu local et ne sont pas publiées.",
    formRequiredHint: "* Champs obligatoires",
    formOverviewTitle: "Votre annonce en un coup d’œil",
    formOverviewText:
      "Complétez les sections dans l’ordre. Vos données sont conservées lorsque vous passez d’une section à l’autre.",
    formPrivacyHint: "Vos coordonnées ne sont pas publiées dans l’annonce.",
    formSectionOffer: "Offre et bien",
    formSectionOfferHint: "Définissez d’abord ce que vous proposez.",
    formSectionLocation: "Adresse",
    formSectionLocationHint: "L’adresse exacte peut rester masquée.",
    formSectionDetails: "Données clés",
    formSectionDetailsHint: "Les informations essentielles pour les personnes intéressées.",
    formSectionPrice: "Prix et disponibilité",
    formSectionPriceHint: "Des informations transparentes facilitent la comparaison.",
    formSectionPresentation: "Description et équipement",
    formSectionPresentationHint: "Montrez ce qui rend votre bien unique.",
    formSectionMedia: "Photos et plan",
    formSectionContact: "Contact",
    formSectionContactHint: "Ces données nous permettent de vous contacter en cas de question.",
    formObjectTitle: "Titre de l'annonce",
    formTitlePlaceholder: "p. ex. Appartement calme avec balcon",
    formTitleHint: "Court et précis – mettez les principaux atouts en premier.",
    formLocation: "Localité",
    formLocationPlaceholder: "p. ex. Muttenz",
    formStreet: "Rue et numéro",
    formStreetPlaceholder: "p. ex. Rue Centrale 12",
    formPostalCode: "NPA",
    formPublishAddress: "Afficher l'adresse exacte dans l'annonce",
    formPublishAddressHint: "Sans sélection, seuls le NPA et la localité seront publiés.",
    formPrice: "Loyer / prix de vente en CHF",
    formAdditionalCosts: "Charges en CHF",
    formAvailability: "Disponible dès",
    formRooms: "Pièces",
    formArea: "Surface habitable en m²",
    formFloor: "Étage",
    formFloorPlaceholder: "p. ex. 2e étage",
    formBathrooms: "Salles de bains / WC",
    formConstructionYear: "Année de construction",
    formCondition: "État",
    formMode: "Type d'offre",
    formKind: "Type de bien",
    formSubkind: "Catégorie de bien",
    formSubkindPlaceholder: "Veuillez sélectionner",
    formDescriptionLabel: "Description du bien",
    formDescriptionMinimum: "Au moins 40 caractères",
    formDescriptionPlaceholder:
      "Décrivez la distribution, la cuisine et les salles de bains, les particularités et les environs.",
    formAmenities: "Équipement et caractéristiques",
    formAmenitiesHint: "Plusieurs choix possibles",
    formPhotos: "Photos (max. 7)",
    formPhotosLimitError: "Veuillez sélectionner au maximum 7 images.",
    formPhotosRequiredError: "Veuillez ajouter au moins une image pour l’annonce.",
    formPhotosHint: "JPG, PNG ou WebP. La première image sera utilisée comme image principale.",
    formFloorPlan: "Plan ou documentation",
    formMediaHint:
      "Facultatif pour l'aperçu. Utilisez uniquement des fichiers dont vous détenez les droits.",
    formContactName: "Personne de contact",
    formContactEmail: "Adresse e-mail",
    formContactPhone: "Numéro de téléphone",
    formCancel: "Annuler",
    formSubmit: "Afficher le brouillon",
    formSubmitHint: "Vous pourrez vérifier toutes les informations avant l’envoi.",
    formErrorRequired: "Veuillez remplir ce champ obligatoire.",
    formErrorPostalCode: "Veuillez saisir un NPA suisse à quatre chiffres.",
    formErrorEmail: "Veuillez saisir une adresse e-mail valide.",
    formErrorPositiveNumber: "Veuillez saisir une valeur supérieure à 0.",
    formErrorNonNegativeNumber: "Veuillez saisir une valeur égale ou supérieure à 0.",
    formErrorYear: "Veuillez saisir une année entre 1500 et 2100.",
    formErrorSummary: "Veuillez vérifier les champs signalés.",
    formSuccess: "Votre brouillon a été ajouté à l'aperçu.",
    draftDescription: "Votre brouillon personnel dans l'aperçu actuel.",
  },
  it: {
    locale: "it-CH",
    heroTitle: "Spazi per ciò",
    heroHighlight: "che verrà dopo.",
    heroText:
      "Scopra appartamenti e case della sua regione, presentati con chiarezza e facili da confrontare.",
    heroTrust: ["Per annunci privati", "Trasparente e locale", "Ricerca gratuita"],
    searchLabel: "Cerca immobili",
    locationPlaceholder: "Località, regione o NPA",
    searchButton: "Trovare immobili",
    featured: "Da scoprire",
    featuredHint: "Casa familiare a Reinach BL",
    featuredPrice: "CHF 1’480’000",
    mode: { all: "Tutti", buy: "Acquistare", rent: "Affittare" },
    propertyTypes: {
      apartment: "Appartamento",
      house: "Casa",
      apartmentBuilding: "Casa plurifamiliare",
      land: "Terreno",
      hospitality: "Gastronomia",
      commercial: "Commercio/industria",
      parking: "Parcheggio",
      chalet: "Chalet",
    },
    allTypes: "Tutti i tipi",
    anyRooms: "Qualsiasi numero di locali",
    roomsPlus: "locali",
    anyBudget: "Qualsiasi budget",
    chooseModeForBudget: "Scegliere acquisto o affitto",
    upTo: "Fino a",
    discoverEyebrow: "Dalla regione",
    discoverTitle: "Immobili in cui sentirsi a casa",
    discoverText:
      "Cerchi in modo mirato o si lasci ispirare da immobili selezionati della Svizzera nordoccidentale.",
    demoNote: "Anteprima con annunci di esempio",
    resultOne: "immobile",
    resultMany: "immobili",
    filters: "Filtri",
    filterHint: "Restringa la selezione secondo i suoi desideri.",
    offerType: "Offerta",
    objectType: "Tipo di immobile",
    rooms: "Locali",
    maxPrice: "Prezzo massimo",
    features: "Dotazioni",
    reset: "Reimpostare tutto",
    mobileFilters: "Mostrare i filtri",
    showResults: "Mostrare i risultati",
    sort: "Ordina",
    sortLabels: {
      newest: "Più recenti",
      "price-asc": "Prezzo crescente",
      "price-desc": "Prezzo decrescente",
      "area-desc": "Superficie maggiore",
    },
    amenities: {
      balcony: "Balcone / terrazza",
      garden: "Giardino",
      parking: "Posteggio / garage",
      lift: "Ascensore",
      pets: "Animali ammessi",
      accessible: "Accessibile in sedia a rotelle",
      dishwasher: "Lavastoviglie",
      washingMachine: "Colonna lavatrice privata",
    },
    conditionLabels: {
      new: "Nuovo / prima occupazione",
      renovated: "Ristrutturato",
      good: "Buono stato",
      needsRenovation: "Da ristrutturare",
    },
    filterFor: "Filtro",
    noResultsTitle: "L'immobile giusto non è ancora qui",
    noResultsText: "Modifichi un filtro o reimposti la selezione per vedere più immobili.",
    clearFilters: "Reimpostare i filtri",
    demoBadge: "Esempio",
    draftBadge: "La sua bozza",
    privateBadge: "Anteprima annuncio",
    monthly: "/ mese",
    additionalCosts: "Spese accessorie",
    available: "Disponibile previo accordo",
    availableFrom: "Disponibile dal",
    bathrooms: "Bagni",
    constructionYear: "Anno di costruzione",
    condition: "Stato",
    floor: "Piano",
    details: "Dettagli",
    save: "Salvare l'immobile",
    unsave: "Rimuovere dai preferiti",
    detailEyebrow: "Anteprima immobiliare",
    detailDescription: "Questo annuncio fittizio mostra la nuova vista immobiliare.",
    contact: "Segnalare interesse",
    contactToast: "Interesse registrato – il contatto sarà attivo al lancio.",
    close: "Chiudere",
    ctaEyebrow: "Spazio per il suo annuncio",
    ctaTitle: "Il suo immobile. Presentato al meglio.",
    ctaText:
      "Crei una bozza in pochi passaggi e veda subito come potrebbe apparire il suo immobile su Begner.",
    ctaButton: "Pubblicare un immobile",
    ctaSecondary: "Scoprire gli annunci",
    agentRegister: "Registrarsi come agente immobiliare",
    pricingLink: "Vedi i prezzi degli annunci",
    formTitle: "Creare una bozza dell'annuncio",
    formDescription: "I dati servono solo per questa anteprima locale e non vengono pubblicati.",
    formRequiredHint: "* Campi obbligatori",
    formOverviewTitle: "Il suo annuncio in sintesi",
    formOverviewText:
      "Compili le sezioni in ordine. I dati restano memorizzati quando passa da una sezione all’altra.",
    formPrivacyHint: "I suoi dati di contatto non saranno pubblicati nell’annuncio.",
    formSectionOffer: "Offerta e immobile",
    formSectionOfferHint: "Definisca innanzitutto cosa desidera offrire.",
    formSectionLocation: "Indirizzo",
    formSectionLocationHint: "L’indirizzo esatto può rimanere nascosto.",
    formSectionDetails: "Dati principali",
    formSectionDetailsHint: "Le informazioni più importanti per gli interessati.",
    formSectionPrice: "Prezzo e disponibilità",
    formSectionPriceHint: "Informazioni trasparenti facilitano il confronto.",
    formSectionPresentation: "Descrizione e dotazioni",
    formSectionPresentationHint: "Mostri cosa rende speciale il suo immobile.",
    formSectionMedia: "Foto e planimetria",
    formSectionContact: "Contatto",
    formSectionContactHint: "Questi dati ci permettono di contattarla in caso di domande.",
    formObjectTitle: "Titolo dell'annuncio",
    formTitlePlaceholder: "es. Appartamento tranquillo con balcone",
    formTitleHint: "Breve e concreto – indichi prima i vantaggi principali.",
    formLocation: "Località",
    formLocationPlaceholder: "es. Muttenz",
    formStreet: "Via e numero civico",
    formStreetPlaceholder: "es. Via Centrale 12",
    formPostalCode: "NPA",
    formPublishAddress: "Mostrare l'indirizzo esatto nell'annuncio",
    formPublishAddressHint: "Senza selezione saranno pubblicati solo NPA e località.",
    formPrice: "Affitto / prezzo di vendita in CHF",
    formAdditionalCosts: "Spese accessorie in CHF",
    formAvailability: "Disponibile dal",
    formRooms: "Locali",
    formArea: "Superficie abitabile in m²",
    formFloor: "Piano",
    formFloorPlaceholder: "es. 2° piano",
    formBathrooms: "Bagni / WC",
    formConstructionYear: "Anno di costruzione",
    formCondition: "Stato",
    formMode: "Tipo di offerta",
    formKind: "Tipo di immobile",
    formSubkind: "Categoria dell'immobile",
    formSubkindPlaceholder: "Selezionare",
    formDescriptionLabel: "Descrizione dell'immobile",
    formDescriptionMinimum: "Almeno 40 caratteri",
    formDescriptionPlaceholder:
      "Descriva la disposizione, cucina e bagni, le particolarità e i dintorni.",
    formAmenities: "Dotazioni e caratteristiche",
    formAmenitiesHint: "Sono possibili più scelte",
    formPhotos: "Foto (max. 7)",
    formPhotosLimitError: "Selezioni al massimo 7 immagini.",
    formPhotosRequiredError: "Carichi almeno un’immagine per l’annuncio.",
    formPhotosHint: "JPG, PNG o WebP. La prima immagine sarà usata come immagine principale.",
    formFloorPlan: "Planimetria o documentazione",
    formMediaHint: "Facoltativo per l'anteprima. Utilizzi solo file di cui possiede i diritti.",
    formContactName: "Persona di contatto",
    formContactEmail: "Indirizzo e-mail",
    formContactPhone: "Numero di telefono",
    formCancel: "Annullare",
    formSubmit: "Mostrare la bozza",
    formSubmitHint: "Prima dell’invio potrà controllare nuovamente tutti i dati.",
    formErrorRequired: "Compili questo campo obbligatorio.",
    formErrorPostalCode: "Inserisca un NPA svizzero di quattro cifre.",
    formErrorEmail: "Inserisca un indirizzo e-mail valido.",
    formErrorPositiveNumber: "Inserisca un valore superiore a 0.",
    formErrorNonNegativeNumber: "Inserisca un valore pari o superiore a 0.",
    formErrorYear: "Inserisca un anno compreso tra 1500 e 2100.",
    formErrorSummary: "Controlli i campi evidenziati.",
    formSuccess: "La bozza è stata aggiunta all'anteprima.",
    draftDescription: "La sua bozza personale nell’anteprima attuale.",
  },
  en: {
    locale: "en-CH",
    heroTitle: "Spaces for",
    heroHighlight: "whatever comes next.",
    heroText:
      "Discover selected apartments and houses in your region — personally listed, clearly presented and easy to compare.",
    heroTrust: ["For private listings", "Transparent & local", "Free to search"],
    searchLabel: "Search properties",
    locationPlaceholder: "Location, region or postcode",
    searchButton: "Find properties",
    featured: "New discovery",
    featuredHint: "Family home in Reinach BL",
    featuredPrice: "CHF 1,480,000",
    mode: { all: "All", buy: "Buy", rent: "Rent" },
    propertyTypes: {
      apartment: "Apartment",
      house: "House",
      apartmentBuilding: "Apartment building",
      land: "Land",
      hospitality: "Hospitality",
      commercial: "Commercial/industrial",
      parking: "Parking",
      chalet: "Chalet",
    },
    allTypes: "All property types",
    anyRooms: "Any number of rooms",
    roomsPlus: "rooms",
    anyBudget: "Any budget",
    chooseModeForBudget: "Choose buy or rent",
    upTo: "Up to",
    discoverEyebrow: "From the region",
    discoverTitle: "Properties that feel right",
    discoverText:
      "Search with precision or find inspiration among selected properties in north-western Switzerland.",
    demoNote: "Preview with sample listings",
    resultOne: "property",
    resultMany: "properties",
    filters: "Filters",
    filterHint: "Narrow the selection to suit your needs.",
    offerType: "Offer",
    objectType: "Property type",
    rooms: "Rooms",
    maxPrice: "Maximum price",
    features: "Features",
    reset: "Reset all",
    mobileFilters: "Show filters",
    showResults: "Show results",
    sort: "Sort",
    sortLabels: {
      newest: "Newest first",
      "price-asc": "Price ascending",
      "price-desc": "Price descending",
      "area-desc": "Largest area",
    },
    amenities: {
      balcony: "Balcony / terrace",
      garden: "Garden",
      parking: "Parking / garage",
      lift: "Lift",
      pets: "Pets allowed",
      accessible: "Wheelchair accessible",
      dishwasher: "Dishwasher",
      washingMachine: "Private washer and dryer",
    },
    conditionLabels: {
      new: "New build / first occupancy",
      renovated: "Renovated",
      good: "Good condition",
      needsRenovation: "In need of renovation",
    },
    filterFor: "Filters for",
    noResultsTitle: "Nothing suitable yet",
    noResultsText: "Change a filter or reset the selection to see more properties.",
    clearFilters: "Reset filters",
    demoBadge: "Sample",
    draftBadge: "Your draft",
    privateBadge: "Listing preview",
    monthly: "/ month",
    additionalCosts: "Additional costs",
    available: "Available by arrangement",
    availableFrom: "Available from",
    bathrooms: "Bathrooms",
    constructionYear: "Year built",
    condition: "Condition",
    floor: "Floor",
    details: "View details",
    save: "Save property",
    unsave: "Remove from saved properties",
    detailEyebrow: "Property preview",
    detailDescription: "This listing is a fictional example for the new property experience.",
    contact: "Register interest",
    contactToast: "Interest saved — the contact feature will follow at launch.",
    close: "Close",
    ctaEyebrow: "Room for your listing",
    ctaTitle: "Your property. Beautifully presented.",
    ctaText:
      "Create a listing draft in a few steps and see straight away how your property could look on Begner.",
    ctaButton: "List a property",
    ctaSecondary: "Discover listings",
    agentRegister: "Register as a real-estate agent",
    pricingLink: "View listing pricing",
    formTitle: "Create a listing draft",
    formDescription: "The details are used only for this local preview and are not yet published.",
    formRequiredHint: "* Required fields",
    formOverviewTitle: "Your listing at a glance",
    formOverviewText:
      "Complete each section in order. Your details remain in place as you move between sections.",
    formPrivacyHint: "Your contact details are not published in the listing.",
    formSectionOffer: "Offer & property",
    formSectionOfferHint: "First define what you are offering.",
    formSectionLocation: "Address",
    formSectionLocationHint: "The exact address can remain hidden.",
    formSectionDetails: "Key details",
    formSectionDetailsHint: "The most important facts for prospective customers.",
    formSectionPrice: "Price & availability",
    formSectionPriceHint: "Transparent details make comparison easier.",
    formSectionPresentation: "Description & features",
    formSectionPresentationHint: "Show what makes your property special.",
    formSectionMedia: "Images & floor plan",
    formSectionContact: "Contact",
    formSectionContactHint: "This lets us clarify any questions about your listing.",
    formObjectTitle: "Listing title",
    formTitlePlaceholder: "e.g. Peaceful apartment with balcony",
    formTitleHint: "Short and specific — lead with the key benefits.",
    formLocation: "Location",
    formLocationPlaceholder: "e.g. Muttenz",
    formStreet: "Street and house number",
    formStreetPlaceholder: "e.g. Hauptstrasse 12",
    formPostalCode: "Postcode",
    formPublishAddress: "Show exact address in listing",
    formPublishAddressHint: "Without this selection, only the postcode and location are published.",
    formPrice: "Rent / purchase price in CHF",
    formAdditionalCosts: "Additional costs in CHF",
    formAvailability: "Available from",
    formRooms: "Rooms",
    formArea: "Living area in m²",
    formFloor: "Floor",
    formFloorPlaceholder: "e.g. 2nd floor",
    formBathrooms: "Bathrooms / WC",
    formConstructionYear: "Year built",
    formCondition: "Condition",
    formMode: "Offer type",
    formKind: "Property type",
    formSubkind: "Property category",
    formSubkindPlaceholder: "Please select",
    formDescriptionLabel: "Property description",
    formDescriptionMinimum: "At least 40 characters",
    formDescriptionPlaceholder:
      "Describe the layout, kitchen and bathrooms, special features and surroundings.",
    formAmenities: "Features and amenities",
    formAmenitiesHint: "Multiple selection possible",
    formPhotos: "Photos (max. 7)",
    formPhotosLimitError: "Please select no more than 7 images.",
    formPhotosRequiredError: "Please upload at least one image for the listing.",
    formPhotosHint: "JPG, PNG or WebP. The first image is used as the cover image.",
    formFloorPlan: "Floor plan or documentation",
    formMediaHint: "Optional for the preview. Use only files for which you hold the rights.",
    formContactName: "Contact person",
    formContactEmail: "Email address",
    formContactPhone: "Phone number",
    formCancel: "Cancel",
    formSubmit: "Show draft",
    formSubmitHint: "You can review all details before submission.",
    formErrorRequired: "Please complete this required field.",
    formErrorPostalCode: "Please enter a four-digit Swiss postcode.",
    formErrorEmail: "Please enter a valid email address.",
    formErrorPositiveNumber: "Please enter a value greater than 0.",
    formErrorNonNegativeNumber: "Please enter 0 or a higher value.",
    formErrorYear: "Please enter a year between 1500 and 2100.",
    formErrorSummary: "Please check the highlighted fields.",
    formSuccess: "Your listing draft has been added to the preview.",
    draftDescription: "Your personal listing draft in the current preview.",
  },
} as const;

type RealEstateCopy = (typeof realEstateCopy)[LanguageCode];

const propertyPlanCodes = ["property_14_days", "property_30_days"] as const;
type PropertyPlanCode = (typeof propertyPlanCodes)[number];

const listingPackageCopy = {
  de: {
    title: "Laufzeit wählen",
    text: "Bezahlt wird erst nach unserer Prüfung per QR-Rechnung oder Banküberweisung. Die Laufzeit beginnt mit der Freischaltung.",
    selected: "Ausgewählt",
    oneTime: "einmalig",
  },
  fr: {
    title: "Choisir la durée",
    text: "Le paiement intervient après notre vérification par facture QR ou virement. La durée commence à l’activation.",
    selected: "Sélectionné",
    oneTime: "paiement unique",
  },
  it: {
    title: "Scegliere la durata",
    text: "Il pagamento avviene dopo la nostra verifica tramite fattura QR o bonifico. La durata inizia con l’attivazione.",
    selected: "Selezionato",
    oneTime: "una tantum",
  },
  en: {
    title: "Choose a duration",
    text: "Payment is due after our review by QR invoice or bank transfer. The duration starts when the listing goes live.",
    selected: "Selected",
    oneTime: "one time",
  },
} satisfies Record<LanguageCode, Record<string, string>>;

const modeOptions: ModeFilter[] = ["all", "buy", "rent"];
const amenityOptions: PropertyAmenity[] = ["balcony", "garden", "parking", "lift"];
const listingFormAmenityOptions: PropertyAmenity[] = [
  "balcony",
  "garden",
  "parking",
  "lift",
  "pets",
  "accessible",
  "dishwasher",
  "washingMachine",
];
const conditionOptions: PropertyCondition[] = ["new", "renovated", "good", "needsRenovation"];
const roomOptions = ["2", "3", "4", "5"];

const amenityIcons = {
  balcony: Layers3,
  garden: Trees,
  parking: Car,
  lift: Building2,
  pets: Check,
  accessible: Check,
  dishwasher: Check,
  washingMachine: Check,
} satisfies Record<PropertyAmenity, typeof Building2>;

function formatPrice(value: number) {
  const groupedValue = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "’");
  return `CHF ${groupedValue}`;
}

function formatAvailability(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale).format(new Date(`${value}T12:00:00`));
}

function ModeSwitch({
  value,
  onChange,
  copy,
  compact = false,
}: {
  value: ModeFilter;
  onChange: (mode: ModeFilter) => void;
  copy: RealEstateCopy;
  compact?: boolean;
}) {
  return (
    <div className="inline-flex rounded-xl bg-muted p-1" role="group" aria-label={copy.offerType}>
      {modeOptions.map((option) => {
        const active = value === option;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option)}
            className={`rounded-lg font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"
            } ${
              active
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {copy.mode[option]}
          </button>
        );
      })}
    </div>
  );
}

function PropertyCard({
  property,
  language,
  copy,
  favorite,
  onFavorite,
  onOpen,
  index,
}: {
  property: PropertyListing;
  language: LanguageCode;
  copy: RealEstateCopy;
  favorite: boolean;
  onFavorite: () => void;
  onOpen: () => void;
  index: number;
}) {
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("button, a, input, label, select, textarea"))
          return;
        onOpen();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      aria-label={`${copy.details}: ${property.title[language]}`}
      className="property-card-enter group flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-xl border border-border/90 bg-card shadow-card transition duration-300 hover:-translate-y-1 hover:border-brand/60 hover:shadow-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ animationDelay: `${Math.min(index * 70, 350)}ms` }}
    >
      <div className="relative aspect-[16/7] overflow-hidden bg-muted">
        <img
          src={property.image}
          alt={property.imageAlt[language]}
          loading="lazy"
          className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.045]"
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-2.5">
          <div className="flex flex-wrap gap-2">
            <Badge className="border-0 bg-background/90 text-foreground shadow-sm backdrop-blur hover:bg-background/90">
              {property.draft ? copy.draftBadge : copy.demoBadge}
            </Badge>
            {property.featured && (
              <Badge className="border-0 bg-brand-deep text-white shadow-sm hover:bg-brand-deep">
                {copy.featured}
              </Badge>
            )}
          </div>
          <button
            type="button"
            aria-label={`${favorite ? copy.unsave : copy.save}: ${property.title[language]}`}
            aria-pressed={favorite}
            onClick={onFavorite}
            className={`grid h-8 w-8 place-items-center rounded-full border border-white/70 bg-background/90 shadow-sm backdrop-blur transition duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              favorite ? "text-rose-500" : "text-foreground"
            }`}
          >
            <Heart className={`h-4.5 w-4.5 ${favorite ? "fill-current" : ""}`} />
          </button>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-sm font-medium text-white">
          <MapPin className="h-4 w-4" />
          {property.location}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-3">
        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-brand-deep">
          <span>
            {copy.propertyTypes[property.kind]}
            {property.subkind ? ` · ${property.subkind}` : ""}
          </span>
          <span className="normal-case tracking-normal text-muted-foreground">
            {copy.privateBadge}
          </span>
        </div>
        <h3 className="mt-2 line-clamp-2 font-display text-base font-bold leading-snug text-foreground transition-colors group-hover:text-brand-deep">
          {property.title[language]}
        </h3>
        <p className="mt-1 line-clamp-1 text-xs leading-relaxed text-muted-foreground">
          {property.description[language]}
        </p>

        <div className="mt-2.5 grid grid-cols-2 gap-x-2 gap-y-1.5 border-t border-border/70 pt-2.5 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <BedDouble className="h-4 w-4 text-brand-deep" />
            {property.rooms} {copy.rooms}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Ruler className="h-4 w-4 text-brand-deep" />
            {property.area} m²
          </span>
        </div>

        <div className="mt-2.5 flex items-end justify-between gap-3">
          <div>
            <p className="font-display text-base font-extrabold text-foreground">
              {formatPrice(property.price)}
            </p>
            {property.mode === "rent" && (
              <p className="mt-0.5 text-xs text-muted-foreground">{copy.monthly}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onOpen}
            aria-label={`${copy.details}: ${property.title[language]}`}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {copy.details}
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function PropertyGallery({
  property,
  alt,
  badge,
}: {
  property: PropertyListing;
  alt: string;
  badge: string;
}) {
  const images = property.images?.length ? property.images : [property.image];
  const [active, setActive] = useState(0);
  const current = images[Math.min(active, images.length - 1)] ?? property.image;

  return (
    <div className="shrink-0">
      <div className="relative h-40 overflow-hidden rounded-t-2xl bg-muted sm:h-48 md:h-52">
        <img src={current} alt={alt} className="h-full w-full object-cover" />
        <Badge className="absolute left-4 top-4 border-0 bg-background/90 text-foreground hover:bg-background/90">
          {badge}
        </Badge>
        {images.length > 1 && (
          <span className="absolute bottom-3 right-4 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground">
            {Math.min(active, images.length - 1) + 1} / {images.length}
          </span>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto border-b border-border/70 bg-card px-4 py-2.5">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`${alt} ${index + 1}`}
              className={`h-12 w-18 shrink-0 overflow-hidden rounded-md border transition ${
                index === Math.min(active, images.length - 1)
                  ? "border-brand ring-2 ring-brand/40"
                  : "border-border/80 hover:border-brand/60"
              }`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RealEstateAgentCard({
  agent,
  copy,
  language,
  isSample,
}: {
  agent: ProviderRow;
  copy: (typeof agentProfileCopy)[LanguageCode];
  language: LanguageCode;
  isSample: boolean;
}) {
  const portrait = agent.image_url || (isSample ? agentPortrait : null);
  const initials = agent.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toLocaleUpperCase();
  const rating =
    agent.rating !== null
      ? new Intl.NumberFormat(language === "de" ? "de-CH" : `${language}-CH`, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(agent.rating)
      : null;
  const websiteHref = normalizeExternalUrl(agent.website);
  const websiteLabel = formatWebsiteLabel(agent.website);

  return (
    <article className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-[0_14px_35px_-28px_oklch(0.25_0.03_235_/_0.35)] sm:grid sm:min-h-[290px] sm:grid-cols-[minmax(130px,0.5fr)_minmax(0,1.5fr)]">
      <div className="grid min-h-52 grid-rows-[auto_minmax(10rem,1fr)_auto] overflow-hidden bg-surface-strong sm:min-h-full">
        <div className="flex flex-wrap gap-1.5 px-3 pt-3">
          {agent.verified ? (
            <Badge className="border border-border bg-card text-brand-deep shadow-none hover:bg-card">
              <BadgeCheck className="mr-1 h-3.5 w-3.5" />
              {copy.verified}
            </Badge>
          ) : null}
          {isSample ? (
            <Badge className="border border-white/15 bg-foreground/70 text-white shadow-none hover:bg-foreground/70">
              {copy.sample}
            </Badge>
          ) : null}
        </div>
        <div className="relative min-h-40">
          {portrait ? (
            <img
              src={portrait}
              alt={`${copy.agent} ${agent.name}`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-contain p-4"
            />
          ) : (
            <div className="absolute inset-4 grid place-items-center rounded-xl bg-gradient-brand text-5xl font-bold text-white/95">
              {initials}
            </div>
          )}
        </div>
        {agent.location ? (
          <div className="flex items-center gap-1.5 border-t border-border p-4 text-xs font-semibold text-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span>{agent.location}</span>
          </div>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col p-3.5 sm:p-4">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-2.5">
            <div className="min-w-0">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-brand-deep">
                {copy.agent}
              </p>
              <h3 className="mt-1 font-display text-lg font-bold leading-tight text-foreground">
                {agent.name}
              </h3>
              {agent.category ? (
                <p className="mt-0.5 text-xs font-medium text-muted-foreground">{agent.category}</p>
              ) : null}
            </div>
            {rating ? (
              <div className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-bold text-foreground ring-1 ring-border">
                <Star className="h-3 w-3 fill-muted-foreground text-muted-foreground" />
                {rating}
                {agent.review_count > 0 ? (
                  <span className="font-medium text-muted-foreground">
                    · {agent.review_count} {copy.reviews}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {agent.description ? (
            <p className="mt-2.5 text-xs leading-[1.15rem] text-muted-foreground">
              {agent.description}
            </p>
          ) : null}

          {agent.services.length > 0 ? (
            <div className="mt-3.5">
              <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {copy.services}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {agent.services.slice(0, 3).map((service) => (
                  <span
                    key={service}
                    className="rounded-md border border-border bg-muted/60 px-2 py-1 text-[0.7rem] font-semibold text-foreground/80"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-4 border-t border-border pt-3.5">
          <p className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {copy.contact}
          </p>
          <div className="mt-2 grid gap-1.5 text-xs">
            {agent.phone ? (
              <a
                href={`tel:${agent.phone.replace(/\s/g, "")}`}
                className="flex min-w-0 items-center gap-2 rounded-lg bg-muted/55 px-2.5 py-2 font-semibold text-foreground transition hover:bg-muted"
              >
                <Phone className="h-3.5 w-3.5 shrink-0 text-brand-deep" />
                <span className="truncate">{agent.phone}</span>
              </a>
            ) : null}
            {agent.email ? (
              <a
                href={`mailto:${agent.email}`}
                className="flex min-w-0 items-center gap-2 rounded-lg bg-muted/55 px-2.5 py-2 font-semibold text-foreground transition hover:bg-muted"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 text-brand-deep" />
                <span className="truncate">{agent.email}</span>
              </a>
            ) : null}
            {websiteHref ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 items-center gap-2 rounded-lg bg-muted/55 px-2.5 py-2 font-semibold text-foreground transition hover:bg-muted"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-brand-deep" />
                <span className="truncate">{websiteLabel || copy.website}</span>
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function normalizeExternalUrl(value: string | null): string | null {
  if (!value) return null;
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function formatWebsiteLabel(value: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(normalizeExternalUrl(value) ?? value).hostname.replace(/^www\./, "");
  } catch {
    return value;
  }
}

function ImmobilienPage() {
  const { language } = useLanguage();
  const { paket } = Route.useSearch();
  const messages = emailMessages[language];
  const sendListing = useServerFn(submitForm);
  const [listingDiscount, setListingDiscount] = useState<DiscountSelection | null>(null);
  const [listingRequestId, setListingRequestId] = useState(() => crypto.randomUUID());
  const [sendingListing, setSendingListing] = useState(false);
  const copy = realEstateCopy[language];
  const [mode, setMode] = useState<ModeFilter>("all");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [rooms, setRooms] = useState("all");
  const [maxPrice, setMaxPrice] = useState("all");
  const [amenities, setAmenities] = useState<PropertyAmenity[]>([]);
  const [sort, setSort] = useState<SortOption>("newest");
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<PropertyListing | null>(null);
  const [listingDialogOpen, setListingDialogOpen] = useState(false);
  const [listingPlanCode, setListingPlanCode] = useState<PropertyPlanCode>(
    paket ?? "property_30_days",
  );
  const [listingFormErrors, setListingFormErrors] = useState<Record<string, string>>({});
  const [listingDescriptionLength, setListingDescriptionLength] = useState(0);
  const [listingKind, setListingKind] = useState<(typeof PROPERTY_FORM_KINDS)[number]>("apartment");
  const [listingSubkind, setListingSubkind] = useState("");
  const [selectedPhotoCount, setSelectedPhotoCount] = useState(0);
  const listingFormScrollRef = useRef<HTMLDivElement>(null);
  const uploadImages = useServerFn(uploadFormImages);
  const loadListings = useServerFn(getPublicListings);
  const loadProviders = useServerFn(getPublicProviders);
  const [published, setPublished] = useState<PropertyListing[]>([]);
  const [agents, setAgents] = useState<ProviderRow[]>([]);
  const displayedAgents = agents.length > 0 ? agents : [demoAgent];
  const agentCopy = agentProfileCopy[language];
  const packageCopy = listingPackageCopy[language];
  const propertyPlans = propertyPlanCodes.map(
    (code) => fallbackBillingPlans.find((plan) => plan.code === code)!,
  );
  const listingSubtypes = PROPERTY_SUBTYPES[listingKind];

  useEffect(() => {
    if (!paket) return;
    setListingPlanCode(paket);
    setListingDialogOpen(true);
  }, [paket]);

  useEffect(() => {
    let active = true;
    void loadListings()
      .then((res) => {
        if (!active) return;
        setPublished(
          res.listings.map((row) => ({
            id: row.id,
            mode: (row.mode as ListingMode) ?? "rent",
            kind: (row.kind as PropertyKind) ?? "apartment",
            subkind: row.subkind ?? undefined,
            title: { de: row.title, fr: row.title, it: row.title, en: row.title },
            description: {
              de: row.description,
              fr: row.description,
              it: row.description,
              en: row.description,
            },
            location: row.location,
            postalCode: row.postal_code ?? undefined,
            street: row.street ?? undefined,
            region: row.region ?? row.postal_code ?? "",
            price: Number(row.price),
            additionalCosts: row.additional_costs ? Number(row.additional_costs) : undefined,
            rooms: Number(row.rooms),
            area: Number(row.area),
            floor: row.floor ?? undefined,
            bathrooms: row.bathrooms ? Number(row.bathrooms) : undefined,
            constructionYear: row.construction_year ?? undefined,
            condition: (row.condition as PropertyCondition) ?? undefined,
            availability: row.availability ?? undefined,
            addressPublished: row.address_published,
            contactName: row.contact_name ?? undefined,
            contactEmail: row.contact_email ?? undefined,
            contactPhone: row.contact_phone ?? undefined,
            amenities: (row.amenities ?? []) as PropertyAmenity[],
            image: row.images?.[0] ?? propertyListings[0].image,
            images: (row.images ?? []).filter(Boolean),
            imageAlt: { de: row.title, fr: row.title, it: row.title, en: row.title },
            publishedOrder: new Date(row.created_at).getTime(),
          })),
        );
      })
      .catch(() => setPublished([]));
    void loadProviders({ data: { type: "immobilienmakler" } })
      .then((res) => {
        if (active) setAgents(res.providers);
      })
      .catch(() => setAgents([]));
    return () => {
      active = false;
    };
  }, [loadListings, loadProviders]);

  const allListings = published;

  const priceOptions = useMemo(() => {
    const values =
      mode === "rent"
        ? [2000, 2500, 3000, 4000]
        : mode === "buy"
          ? [750000, 1000000, 1500000, 2000000]
          : [];

    return values.map((value) => ({
      value: String(value),
      label: `${copy.upTo} ${formatPrice(value)}`,
    }));
  }, [copy, mode]);

  const filteredListings = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase(copy.locale);
    const result = allListings.filter((property) => {
      const matchesQuery =
        !normalizedQuery ||
        [
          property.title[language],
          property.description[language],
          property.location,
          property.postalCode,
          property.street,
          property.region,
        ]
          .join(" ")
          .toLocaleLowerCase(copy.locale)
          .includes(normalizedQuery);
      const matchesMode = mode === "all" || property.mode === mode;
      const matchesKind = kind === "all" || property.kind === kind;
      const matchesRooms = rooms === "all" || property.rooms >= Number(rooms);
      const matchesPrice = maxPrice === "all" || property.price <= Number(maxPrice);
      const matchesAmenities = amenities.every((amenity) => property.amenities.includes(amenity));

      return (
        matchesQuery &&
        matchesMode &&
        matchesKind &&
        matchesRooms &&
        matchesPrice &&
        matchesAmenities
      );
    });

    return [...result].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "area-desc") return b.area - a.area;
      return b.publishedOrder - a.publishedOrder;
    });
  }, [allListings, amenities, copy.locale, kind, language, maxPrice, mode, query, rooms, sort]);

  const activeFilterCount =
    Number(mode !== "all") +
    Number(query.trim().length > 0) +
    Number(kind !== "all") +
    Number(rooms !== "all") +
    Number(maxPrice !== "all") +
    amenities.length;

  const availableSortOptions = (Object.keys(copy.sortLabels) as SortOption[]).filter(
    (option) => mode !== "all" || !option.startsWith("price"),
  );

  const resetFilters = () => {
    setMode("all");
    setQuery("");
    setKind("all");
    setRooms("all");
    setMaxPrice("all");
    setAmenities([]);
    setSort("newest");
  };

  const changeMode = (nextMode: ModeFilter) => {
    setMode(nextMode);
    setMaxPrice("all");
    if (nextMode === "all" && sort.startsWith("price")) setSort("newest");
  };

  const scrollToListings = () => {
    document.getElementById("objekte")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleAmenity = (amenity: PropertyAmenity, checked: boolean) => {
    setAmenities((current) =>
      checked ? [...new Set([...current, amenity])] : current.filter((item) => item !== amenity),
    );
  };

  const toggleFavorite = (id: string) => {
    setFavorites((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const draftMode = (form.get("mode") || "rent") as ListingMode;
    const draftKind = (form.get("kind") || "apartment") as (typeof PROPERTY_FORM_KINDS)[number];
    const draftSubkind = String(form.get("subkind") || "").trim();
    const title = String(form.get("title") || "").trim();
    const location = String(form.get("location") || "").trim();
    const postalCode = String(form.get("postalCode") || "").trim();
    const street = String(form.get("street") || "").trim();
    const price = Number(form.get("price"));
    const additionalCosts = Number(form.get("additionalCosts"));
    const draftRooms = Number(form.get("rooms"));
    const area = Number(form.get("area"));
    const floor = String(form.get("floor") || "").trim();
    const bathrooms = Number(form.get("bathrooms"));
    const constructionYear = Number(form.get("constructionYear"));
    const condition = (form.get("condition") || "good") as PropertyCondition;
    const availability = String(form.get("availability") || "");
    const description = String(form.get("description") || "").trim();
    const contactName = String(form.get("contactName") || "").trim();
    const contactEmail = String(form.get("contactEmail") || "").trim();
    const contactPhone = String(form.get("contactPhone") || "").trim();
    const draftAmenities = form.getAll("amenities") as PropertyAmenity[];
    const imageFiles = form
      .getAll("photos")
      .filter((file): file is File => file instanceof File && file.size > 0);

    const errors: Record<string, string> = {};
    const requireValue = (name: string, value: string) => {
      if (!value) errors[name] = copy.formErrorRequired;
    };

    requireValue("title", title);
    requireValue("street", street);
    requireValue("postalCode", postalCode);
    requireValue("location", location);
    requireValue("availability", availability);
    requireValue("description", description);
    requireValue("contactName", contactName);
    requireValue("contactEmail", contactEmail);
    if (PROPERTY_SUBTYPES[draftKind].length > 0) requireValue("subkind", draftSubkind);

    if (postalCode && !/^\d{4}$/.test(postalCode)) {
      errors.postalCode = copy.formErrorPostalCode;
    }
    if (!Number.isFinite(price) || price <= 0) errors.price = copy.formErrorPositiveNumber;
    if (!Number.isFinite(draftRooms) || draftRooms <= 0) {
      errors.rooms = copy.formErrorPositiveNumber;
    }
    if (!Number.isFinite(area) || area <= 0) errors.area = copy.formErrorPositiveNumber;
    if (description && description.length < 40) {
      errors.description = copy.formDescriptionMinimum;
    }
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      errors.contactEmail = copy.formErrorEmail;
    }

    const additionalCostsRaw = String(form.get("additionalCosts") || "").trim();
    const bathroomsRaw = String(form.get("bathrooms") || "").trim();
    const constructionYearRaw = String(form.get("constructionYear") || "").trim();
    if (additionalCostsRaw && (!Number.isFinite(additionalCosts) || additionalCosts < 0)) {
      errors.additionalCosts = copy.formErrorNonNegativeNumber;
    }
    if (bathroomsRaw && (!Number.isFinite(bathrooms) || bathrooms < 0.5)) {
      errors.bathrooms = copy.formErrorPositiveNumber;
    }
    if (
      constructionYearRaw &&
      (!Number.isFinite(constructionYear) || constructionYear < 1500 || constructionYear > 2100)
    ) {
      errors.constructionYear = copy.formErrorYear;
    }

    const photosExceedLimit = imageFiles.length > MAX_PROPERTY_PHOTOS;
    const photosMissing = imageFiles.length === 0;
    if (photosExceedLimit) errors.photos = copy.formPhotosLimitError;
    if (photosMissing) errors.photos = copy.formPhotosRequiredError;

    if (Object.keys(errors).length > 0) {
      setListingFormErrors(errors);
      const firstInvalidField = Object.keys(errors)[0];
      requestAnimationFrame(() => {
        const control = formElement.elements.namedItem(firstInvalidField);
        if (control instanceof HTMLElement) {
          control.scrollIntoView({ behavior: "smooth", block: "center" });
          control.focus({ preventScroll: true });
        }
      });
      return;
    }

    setListingFormErrors({});

    setListingDialogOpen(false);

    setSendingListing(true);
    try {
      let imageUrls: string[] = [];
      if (imageFiles.length) {
        const uploaded = await uploadImages({ data: { files: await filesToPayload(imageFiles) } });
        imageUrls = uploaded.urls;
      }

      await sendListing({
        data: {
          formType: "immobilie",
          referencePrefix: "IM",
          discountCode: listingDiscount?.code,
          expectedAmount: listingDiscount?.expectedAmount,
          requestId: listingRequestId,
          headline: `Neues Inserat: ${title}`,
          internalSubject: `Neues Inserat: ${title}`,
          customerSubject: copy.formSuccess,
          locale: language,
          customerName: contactName,
          customerEmail: contactEmail,
          entity: {
            billing_plan_code: listingPlanCode,
            mode: draftMode,
            kind: draftKind,
            subkind: draftSubkind || null,
            title,
            description,
            location,
            postal_code: postalCode,
            street,
            region: postalCode,
            price,
            additional_costs: additionalCosts || null,
            rooms: draftRooms,
            area,
            floor: floor || null,
            bathrooms: bathrooms || null,
            construction_year: constructionYear || null,
            condition,
            availability,
            address_published: form.get("publishAddress") === "on",
            contact_name: contactName,
            contact_email: contactEmail,
            contact_phone: contactPhone || null,
            amenities: draftAmenities,
            images: imageUrls,
          },
          sections: [
            {
              title: "Paket",
              fields: [
                { label: "Leistung", value: billingPlanName(listingPlanCode, language) },
                {
                  label: "Preis",
                  value: formatBillingPrice(
                    propertyPlans.find((plan) => plan.code === listingPlanCode)!.priceMinor,
                    copy.locale,
                  ),
                },
              ],
            },
            {
              title: "Objekt",
              fields: [
                { label: "Angebotsart", value: draftMode === "rent" ? "Miete" : "Kauf" },
                { label: "Objekttyp", value: draftKind },
                { label: "Objektart", value: draftSubkind },
                { label: "Titel", value: title },
                { label: "Beschreibung", value: description },
                { label: "Zustand", value: condition },
                { label: "Verfügbar ab", value: availability },
                { label: "Ausstattung", value: draftAmenities.join(", ") },
              ].filter((field) => field.value),
            },
            {
              title: "Adresse & Zahlen",
              fields: [
                { label: "Strasse", value: street },
                { label: "PLZ", value: postalCode },
                { label: "Ort", value: location },
                { label: "Preis", value: `CHF ${price}` },
                {
                  label: "Nebenkosten",
                  value: additionalCosts ? `CHF ${additionalCosts}` : "",
                },
                { label: "Zimmer", value: String(draftRooms) },
                { label: "Fläche", value: `${area} m2` },
                { label: "Etage", value: floor },
                { label: "Badezimmer", value: bathrooms ? String(bathrooms) : "" },
                {
                  label: "Baujahr",
                  value: constructionYear ? String(constructionYear) : "",
                },
              ].filter((field) => field.value),
            },
            {
              title: "Kontakt",
              fields: [
                { label: "Name", value: contactName },
                { label: "E-Mail", value: contactEmail },
                { label: "Telefon", value: contactPhone },
              ].filter((field) => field.value),
            },
          ],
        },
      });
      toast.success(copy.formSuccess, { description: messages.confirmation });
      setListingRequestId(crypto.randomUUID());
      setListingDiscount(null);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message.startsWith("discount_")
          ? discountError(error.message, language)
          : messages.error,
      );
      setListingDialogOpen(true);
    } finally {
      setSendingListing(false);
    }
  };

  const renderFilters = (idPrefix: string) => (
    <div className="space-y-6">
      <div>
        <Label className="mb-2.5 block text-sm font-semibold">{copy.offerType}</Label>
        <ModeSwitch value={mode} onChange={changeMode} copy={copy} compact />
      </div>

      <div>
        <Label className="mb-2.5 block text-sm font-semibold">{copy.objectType}</Label>
        <Select value={kind} onValueChange={(value) => setKind(value as KindFilter)}>
          <SelectTrigger aria-label={copy.objectType} className="h-11 rounded-xl bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{copy.allTypes}</SelectItem>
            {PROPERTY_FORM_KINDS.map((option) => (
              <SelectItem key={option} value={option}>
                {copy.propertyTypes[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-2.5 block text-sm font-semibold">{copy.rooms}</Label>
        <Select value={rooms} onValueChange={setRooms}>
          <SelectTrigger aria-label={copy.rooms} className="h-11 rounded-xl bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{copy.anyRooms}</SelectItem>
            {roomOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}+ {copy.roomsPlus}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-2.5 block text-sm font-semibold">{copy.maxPrice}</Label>
        {mode === "all" ? (
          <div
            aria-disabled="true"
            className="flex h-11 items-center rounded-xl border border-input bg-muted/60 px-3 text-sm text-muted-foreground"
          >
            {copy.chooseModeForBudget}
          </div>
        ) : (
          <Select value={maxPrice} onValueChange={setMaxPrice}>
            <SelectTrigger aria-label={copy.maxPrice} className="h-11 rounded-xl bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{copy.anyBudget}</SelectItem>
              {priceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div>
        <Label className="mb-3 block text-sm font-semibold">{copy.features}</Label>
        <div className="space-y-3">
          {amenityOptions.map((amenity) => {
            const Icon = amenityIcons[amenity];
            const id = `${idPrefix}-${amenity}`;
            return (
              <div key={amenity} className="flex items-center gap-3">
                <Checkbox
                  id={id}
                  checked={amenities.includes(amenity)}
                  onCheckedChange={(checked) => toggleAmenity(amenity, checked === true)}
                />
                <Label
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-2 text-sm font-normal text-muted-foreground"
                >
                  <Icon className="h-4 w-4 text-brand-deep" />
                  {copy.amenities[amenity]}
                </Label>
              </div>
            );
          })}
        </div>
      </div>

      <Button type="button" variant="ghost" className="w-full" onClick={resetFilters}>
        <X className="h-4 w-4" />
        {copy.reset}
      </Button>
    </div>
  );

  const listingFormSections = [
    { id: "form-section-offer", label: copy.formSectionOffer, icon: Building2 },
    { id: "form-section-location", label: copy.formSectionLocation, icon: MapPin },
    { id: "form-section-details", label: copy.formSectionDetails, icon: Ruler },
    { id: "form-section-price", label: copy.formSectionPrice, icon: CircleDollarSign },
    {
      id: "form-section-presentation",
      label: copy.formSectionPresentation,
      icon: FileText,
    },
    { id: "form-section-media", label: copy.formSectionMedia, icon: Images },
    { id: "form-section-contact", label: copy.formSectionContact, icon: Mail },
  ];

  const jumpToFormSection = (id: string) => {
    const scrollContainer = listingFormScrollRef.current;
    const target = document.getElementById(id);
    if (!scrollContainer || !target) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + targetRect.top - containerRect.top - 16,
      behavior: "smooth",
    });
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-border/70 bg-surface">
        <div className="pointer-events-none absolute -left-28 top-10 h-72 w-72 rounded-full bg-brand-glow/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 -top-20 h-96 w-96 rounded-full bg-brand/15 blur-3xl" />
        <div className="container-page relative py-10 md:py-12 lg:py-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_.85fr]">
            <div className="max-w-2xl property-card-enter">
              <h1 className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-foreground sm:text-5xl lg:text-[3.25rem]">
                {copy.heroTitle}
                <span className="mt-1 block bg-gradient-brand bg-clip-text text-transparent">
                  {copy.heroHighlight}
                </span>
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                {copy.heroText}
              </p>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                {copy.heroTrust.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-2 text-sm font-medium text-foreground"
                  >
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-brand-soft text-brand-deep">
                      <Check className="h-3 w-3" />
                    </span>
                    {item}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  className="h-11 rounded-xl px-5 shadow-brand"
                  onClick={() => {
                    setListingFormErrors({});
                    setSelectedPhotoCount(0);
                    setListingDialogOpen(true);
                  }}
                >
                  <House className="h-4 w-4" />
                  {copy.ctaButton}
                </Button>
                <Button
                  variant="outline"
                  className="h-11 rounded-xl px-5"
                  onClick={scrollToListings}
                >
                  {copy.ctaSecondary}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button asChild variant="ghost" className="h-11 rounded-xl px-4 text-brand-deep">
                  <Link to="/registrieren" search={{ typ: "immobilienmakler" }}>
                    <UserRoundPlus className="h-4 w-4" />
                    {copy.agentRegister}
                  </Link>
                </Button>
                <Button asChild variant="ghost" className="h-11 rounded-xl px-4 text-brand-deep">
                  <Link to="/anbieter-preise" search={{ kategorie: "inserate" }}>
                    <CircleDollarSign className="h-4 w-4" />
                    {copy.pricingLink}
                  </Link>
                </Button>
              </div>
            </div>

            <div className="property-hero-float relative mx-auto w-full max-w-md lg:max-w-none">
              <div className="absolute -inset-3 rotate-2 rounded-[2rem] border border-brand/20 bg-background/55 backdrop-blur" />
              <div className="group relative aspect-[4/3] overflow-hidden rounded-[1.75rem] border-4 border-background shadow-brand">
                <img
                  src={propertyListings[0].image}
                  alt={propertyListings[0].imageAlt[language]}
                  className="h-full w-full object-cover transition duration-1000 group-hover:scale-[1.035]"
                />
                <Badge className="absolute left-5 top-5 border-0 bg-background/90 text-foreground shadow-sm backdrop-blur hover:bg-background/90">
                  {copy.demoBadge}
                </Badge>
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 text-white">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
                      {copy.featured}
                    </p>
                    <p className="mt-1 font-display text-lg font-bold">{copy.featuredHint}</p>
                  </div>
                  <p className="rounded-lg bg-white/15 px-3 py-2 font-display text-base font-bold backdrop-blur-md">
                    {copy.featuredPrice}
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-12 -left-3 hidden items-center gap-3 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-card backdrop-blur sm:flex">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-soft text-brand-deep">
                  <KeyRound className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">{copy.mode.buy}</p>
                  <p className="text-sm font-bold text-foreground">5.5 {copy.rooms} · 162 m²</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-9 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-card backdrop-blur md:p-4">
            <p className="sr-only">{copy.searchLabel}</p>
            <div className="mb-3 flex flex-col justify-between gap-3 border-b border-border/70 pb-3 sm:flex-row sm:items-center">
              <ModeSwitch value={mode} onChange={changeMode} copy={copy} />
              <span className="hidden text-xs font-medium text-muted-foreground sm:inline-flex sm:items-center sm:gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-brand-deep" />
                Basel · Baselland · Nordwestschweiz · Schweizweit
              </span>
            </div>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1.45fr)_minmax(150px,.8fr)_minmax(150px,.8fr)_auto]">
              <div className="relative">
                <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={copy.locationPlaceholder}
                  aria-label={copy.locationPlaceholder}
                  className="h-12 rounded-xl border-border/80 bg-surface pl-10 shadow-none"
                />
              </div>
              <Select value={kind} onValueChange={(value) => setKind(value as KindFilter)}>
                <SelectTrigger
                  aria-label={copy.objectType}
                  className="h-12 rounded-xl border-border/80 bg-surface shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{copy.allTypes}</SelectItem>
                  {PROPERTY_FORM_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {copy.propertyTypes[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === "all" ? (
                <div
                  aria-disabled="true"
                  className="flex h-12 items-center rounded-xl border border-border/80 bg-muted/60 px-3 text-sm text-muted-foreground"
                >
                  {copy.chooseModeForBudget}
                </div>
              ) : (
                <Select value={maxPrice} onValueChange={setMaxPrice}>
                  <SelectTrigger
                    aria-label={copy.maxPrice}
                    className="h-12 rounded-xl border-border/80 bg-surface shadow-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{copy.anyBudget}</SelectItem>
                    {priceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button className="h-12 rounded-xl px-5 shadow-brand" onClick={scrollToListings}>
                <Search className="h-4 w-4" />
                {copy.searchButton}
              </Button>
            </div>
          </div>
        </div>
      </section>
      <section className="container-page space-y-6 py-8">
        <LiestalPropertyFlyer />
        <OffMarketBuyerInquiry />
        <div className="grid gap-6 rounded-2xl border border-brand/20 bg-brand-soft/65 p-6 shadow-card md:p-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <h2 className="font-display text-2xl font-bold">Mehrfamilienhaus diskret verkaufen</h2>
            <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
              Sie möchten Ihre Renditeliegenschaft ohne öffentliche Ausschreibung verkaufen? Wir
              begleiten Sie persönlich und vermitteln gezielt an ausgewählte Investoren.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-xl font-semibold shadow-brand">
            <Link to="/mehrfamilienhaus-verkaufen">
              Vertraulich anfragen <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </section>

      <section className="border-b border-border/70 bg-background py-8 sm:py-10">
        <div className="container-page">
          <PropertyPortalStrip />
        </div>
      </section>

      <section id="objekte" className="scroll-mt-20 bg-background py-16 md:py-20">
        <div className="container-page">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHeading
              eyebrow={copy.discoverEyebrow}
              title={copy.discoverTitle}
              description={copy.discoverText}
              className="max-w-2xl"
              descriptionClassName="leading-relaxed"
            />
            <div className="flex flex-wrap items-center gap-2">
              {PROPERTY_FORM_KINDS.map((option) => {
                const Icon =
                  option === "apartment" ? Building2 : option === "house" ? House : Trees;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setKind(kind === option ? "all" : option)}
                    aria-pressed={kind === option}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                      kind === option
                        ? "border-brand bg-brand-soft text-brand-deep"
                        : "border-border bg-card text-muted-foreground hover:border-brand/60 hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {copy.propertyTypes[option]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-4 border-y border-border/70 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div aria-live="polite">
              <span className="font-display text-xl font-bold text-foreground">
                {filteredListings.length}{" "}
                {filteredListings.length === 1 ? copy.resultOne : copy.resultMany}
              </span>
              <span className="ml-2 text-sm text-muted-foreground">· {copy.demoNote}</span>
            </div>
            <div className="flex w-full flex-col gap-2 min-[380px]:flex-row sm:w-auto sm:items-center">
              <Button
                variant="outline"
                className="h-10 w-full rounded-xl min-[380px]:w-auto lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {copy.mobileFilters}
                {activeFilterCount > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-xs font-bold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              <Select value={sort} onValueChange={(value) => setSort(value as SortOption)}>
                <SelectTrigger
                  aria-label={copy.sort}
                  className="h-10 w-full rounded-xl min-[380px]:w-[190px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableSortOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {copy.sortLabels[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {mode !== "all" && (
                <button
                  type="button"
                  onClick={() => changeMode("all")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  {copy.mode[mode]} <X className="h-3 w-3" />
                </button>
              )}
              {query.trim() && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  “{query.trim()}” <X className="h-3 w-3" />
                </button>
              )}
              {kind !== "all" && (
                <button
                  type="button"
                  onClick={() => setKind("all")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  {copy.propertyTypes[kind]} <X className="h-3 w-3" />
                </button>
              )}
              {rooms !== "all" && (
                <button
                  type="button"
                  onClick={() => setRooms("all")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  {rooms}+ {copy.rooms} <X className="h-3 w-3" />
                </button>
              )}
              {maxPrice !== "all" && (
                <button
                  type="button"
                  onClick={() => setMaxPrice("all")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  {copy.upTo} {formatPrice(Number(maxPrice))} <X className="h-3 w-3" />
                </button>
              )}
              {amenities.map((amenity) => (
                <button
                  key={amenity}
                  type="button"
                  onClick={() => toggleAmenity(amenity, false)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-deep transition hover:bg-brand/20"
                >
                  {copy.amenities[amenity]} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 grid items-start gap-8 lg:grid-cols-[250px_minmax(0,1fr)]">
            <aside className="sticky top-24 hidden rounded-2xl border border-border/80 bg-surface p-5 lg:block">
              <div className="mb-6 flex items-start justify-between gap-3 border-b border-border/70 pb-5">
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground">{copy.filters}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {copy.filterHint}
                  </p>
                </div>
                <SlidersHorizontal className="mt-0.5 h-4 w-4 text-brand-deep" />
              </div>
              {renderFilters("desktop-filter")}
            </aside>

            <div>
              {filteredListings.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {filteredListings.map((property, index) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      language={language}
                      copy={copy}
                      favorite={favorites.has(property.id)}
                      onFavorite={() => toggleFavorite(property.id)}
                      onOpen={() => setSelectedProperty(property)}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid min-h-[420px] place-items-center rounded-3xl border border-dashed border-brand/35 bg-brand-soft/40 px-6 text-center">
                  <div className="max-w-md">
                    <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-background text-brand-deep shadow-card">
                      <Search className="h-7 w-7" />
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-bold text-foreground">
                      {copy.noResultsTitle}
                    </h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">
                      {copy.noResultsText}
                    </p>
                    <Button className="mt-6 rounded-xl" onClick={resetFilters}>
                      {copy.clearFilters}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-surface py-12 md:py-16">
        <div className="container-page relative">
          <SectionHeading
            eyebrow={agentCopy.eyebrow}
            title={agentCopy.title}
            description={agentCopy.text}
            className="max-w-3xl"
            descriptionClassName="max-w-2xl text-base leading-6"
          />

          <Button
            asChild
            variant="outline"
            className="mt-6 h-auto min-h-11 max-w-full whitespace-normal rounded-xl bg-card py-3 text-left"
          >
            <Link to="/makler">
              {
                {
                  de: "Maklerverzeichnis nach Kanton und Leistung",
                  fr: "Annuaire des agents par canton et prestation (DE)",
                  it: "Elenco agenti per cantone e servizio (DE)",
                  en: "Agent directory by canton and service (DE)",
                }[language]
              }
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <div className="mt-7 grid gap-5 lg:grid-cols-2">
            {displayedAgents.map((agent) => (
              <RealEstateAgentCard
                key={agent.id}
                agent={agent}
                copy={agentCopy}
                language={language}
                isSample={agent.id === demoAgent.id}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background pb-4 pt-8 md:pt-14">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-hero px-6 py-12 text-primary-foreground shadow-brand md:px-12 md:py-14">
            <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full border border-white/20" />
            <div className="pointer-events-none absolute -right-3 -top-10 h-48 w-48 rounded-full border border-white/20" />
            <div className="relative flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-wider text-white/75">
                  {copy.ctaEyebrow}
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
                  {copy.ctaTitle}
                </h2>
                <p className="mt-4 max-w-xl leading-relaxed text-white/80">{copy.ctaText}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="h-11 rounded-xl bg-white px-5 text-brand-deep shadow-lg hover:bg-white/90"
                  onClick={() => {
                    setListingFormErrors({});
                    setSelectedPhotoCount(0);
                    setListingDialogOpen(true);
                  }}
                >
                  <House className="h-4 w-4" />
                  {copy.ctaButton}
                </Button>
                <Button
                  variant="outline"
                  className="h-11 rounded-xl border-white/35 bg-transparent px-5 text-white hover:bg-white/10 hover:text-white"
                  onClick={scrollToListings}
                >
                  {copy.ctaSecondary}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="right" className="flex w-[92vw] flex-col overflow-y-auto sm:max-w-md">
          <SheetHeader className="pr-8">
            <SheetTitle className="font-display text-xl">{copy.filters}</SheetTitle>
            <SheetDescription>{copy.filterHint}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 py-4">{renderFilters("mobile-filter")}</div>
          <SheetFooter className="sticky bottom-0 -mx-6 border-t border-border bg-background px-6 py-4">
            <Button
              className="w-full rounded-xl"
              onClick={() => {
                setMobileFiltersOpen(false);
                scrollToListings();
              }}
            >
              {copy.showResults}
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-white/20 px-1 text-xs font-bold">
                {filteredListings.length}
              </span>
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(selectedProperty)}
        onOpenChange={(open) => !open && setSelectedProperty(null)}
      >
        {selectedProperty && (
          <DialogContent className="flex max-h-[88dvh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
            <PropertyGallery
              key={selectedProperty.id}
              property={selectedProperty}
              alt={selectedProperty.imageAlt[language]}
              badge={selectedProperty.draft ? copy.draftBadge : copy.demoBadge}
            />

            <div className="min-h-0 overflow-y-auto p-4 sm:p-5">
              <DialogHeader className="text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-deep">
                    {copy.detailEyebrow}
                  </p>
                  {selectedProperty.subkind ? (
                    <Badge variant="secondary" className="rounded-full">
                      {selectedProperty.subkind}
                    </Badge>
                  ) : null}
                </div>
                <DialogTitle className="pt-1.5 pr-7 font-display text-xl leading-tight sm:text-2xl">
                  {selectedProperty.title[language]}
                </DialogTitle>
                <DialogDescription className="line-clamp-3 pt-1.5 text-sm leading-relaxed">
                  {selectedProperty.description[language]}
                  {!selectedProperty.draft && ` ${copy.detailDescription}`}
                </DialogDescription>
                {selectedProperty.addressPublished && selectedProperty.street && (
                  <p className="pt-1.5 text-sm font-medium text-foreground">
                    {selectedProperty.street}, {selectedProperty.postalCode}{" "}
                    {selectedProperty.location}
                  </p>
                )}
              </DialogHeader>
              <div className="mt-4 grid grid-cols-3 divide-x divide-border/80 rounded-xl bg-surface p-3">
                <div className="flex min-w-0 flex-col items-center gap-1 px-1 text-center sm:flex-row sm:gap-2 sm:px-3 sm:text-left">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand-deep">
                    <BedDouble className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs text-muted-foreground">{copy.rooms}</p>
                    <p className="font-bold">{selectedProperty.rooms}</p>
                  </div>
                </div>
                <div className="flex min-w-0 flex-col items-center gap-1 px-1 text-center sm:flex-row sm:gap-2 sm:px-3 sm:text-left">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand-deep">
                    <Ruler className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs text-muted-foreground">m²</p>
                    <p className="font-bold">{selectedProperty.area}</p>
                  </div>
                </div>
                <div className="flex min-w-0 flex-col items-center gap-1 px-1 text-center sm:flex-row sm:gap-2 sm:px-3 sm:text-left">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand-deep">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="hidden text-xs text-muted-foreground sm:block">
                      {selectedProperty.region}
                    </p>
                    <p className="truncate text-sm font-bold">{selectedProperty.location}</p>
                  </div>
                </div>
              </div>
              {(selectedProperty.floor ||
                selectedProperty.bathrooms ||
                selectedProperty.constructionYear ||
                selectedProperty.condition) && (
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-border/70 p-3 sm:grid-cols-4">
                  {selectedProperty.floor && (
                    <div>
                      <p className="text-xs text-muted-foreground">{copy.floor}</p>
                      <p className="text-sm font-bold">{selectedProperty.floor}</p>
                    </div>
                  )}
                  {selectedProperty.bathrooms && (
                    <div>
                      <p className="text-xs text-muted-foreground">{copy.bathrooms}</p>
                      <p className="text-sm font-bold">{selectedProperty.bathrooms}</p>
                    </div>
                  )}
                  {selectedProperty.constructionYear && (
                    <div>
                      <p className="text-xs text-muted-foreground">{copy.constructionYear}</p>
                      <p className="text-sm font-bold">{selectedProperty.constructionYear}</p>
                    </div>
                  )}
                  {selectedProperty.condition && (
                    <div>
                      <p className="text-xs text-muted-foreground">{copy.condition}</p>
                      <p className="text-sm font-bold">
                        {copy.conditionLabels[selectedProperty.condition]}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {selectedProperty.amenities.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {selectedProperty.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="rounded-full bg-brand-soft px-2.5 py-1 text-xs font-semibold text-brand-deep"
                    >
                      {copy.amenities[amenity]}
                    </span>
                  ))}
                </div>
              )}
              {selectedProperty.draft && selectedProperty.contactName && (
                <div className="mt-3 rounded-xl bg-surface p-3.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {copy.formSectionContact}
                  </p>
                  <p className="mt-2 font-bold">{selectedProperty.contactName}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-brand-deep">
                    {selectedProperty.contactEmail && (
                      <a href={`mailto:${selectedProperty.contactEmail}`}>
                        {selectedProperty.contactEmail}
                      </a>
                    )}
                    {selectedProperty.contactPhone && (
                      <a href={`tel:${selectedProperty.contactPhone}`}>
                        {selectedProperty.contactPhone}
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-col justify-between gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
                <div>
                  <p className="font-display text-xl font-extrabold">
                    {formatPrice(selectedProperty.price)}
                  </p>
                  {selectedProperty.mode === "rent" && (
                    <p className="mt-1 text-xs text-muted-foreground">{copy.monthly}</p>
                  )}
                  {selectedProperty.additionalCosts && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      + {formatPrice(selectedProperty.additionalCosts)} {copy.additionalCosts}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-muted-foreground">
                    {selectedProperty.availability
                      ? `${copy.availableFrom} ${formatAvailability(selectedProperty.availability, copy.locale)}`
                      : copy.available}
                  </p>
                </div>
                <DialogFooter className="gap-2 sm:space-x-0">
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    onClick={() => setSelectedProperty(null)}
                  >
                    {copy.close}
                  </Button>
                  <Button
                    className="rounded-xl shadow-brand"
                    onClick={() => {
                      toast.success(copy.contactToast);
                      setSelectedProperty(null);
                    }}
                  >
                    {copy.contact}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </DialogFooter>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={listingDialogOpen}
        onOpenChange={(open) => {
          setListingDialogOpen(open);
          setListingFormErrors({});
          setSelectedPhotoCount(0);
          setListingDescriptionLength(0);
        }}
      >
        <DialogContent className="flex max-h-[94dvh] w-[calc(100%-1rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-3xl p-0">
          <DialogHeader className="shrink-0 border-b border-border/70 bg-background px-5 pb-4 pt-5 pr-12 text-left sm:px-7 sm:pb-5 sm:pt-6">
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
              <div>
                <DialogTitle className="font-display text-2xl font-extrabold sm:text-3xl">
                  {copy.formTitle}
                </DialogTitle>
                <DialogDescription className="mt-1 max-w-2xl leading-relaxed">
                  {copy.formDescription}
                </DialogDescription>
                <p className="mt-2 text-xs font-medium text-muted-foreground sm:hidden">
                  {copy.formRequiredHint}
                </p>
              </div>
              <Badge
                variant="secondary"
                className="hidden shrink-0 rounded-full px-3 py-1.5 font-medium sm:inline-flex"
              >
                {copy.formRequiredHint}
              </Badge>
            </div>
          </DialogHeader>
          <form
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            noValidate
            onSubmit={submitDraft}
            onChangeCapture={(event) => {
              const target = event.target;
              if (
                !(
                  target instanceof HTMLInputElement ||
                  target instanceof HTMLTextAreaElement ||
                  target instanceof HTMLSelectElement
                )
              )
                return;
              if (!target.name || !listingFormErrors[target.name] || !target.validity.valid) return;
              setListingFormErrors((current) => {
                const next = { ...current };
                delete next[target.name];
                return next;
              });
            }}
          >
            <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[15rem_minmax(0,1fr)]">
              <aside className="hidden min-h-0 overflow-y-auto border-r border-border/70 bg-surface/80 p-5 lg:block">
                <div className="rounded-2xl border border-brand/15 bg-background p-4 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand-deep">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <p className="mt-4 font-display text-base font-bold">{copy.formOverviewTitle}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {copy.formOverviewText}
                  </p>
                </div>
                <nav className="mt-5 space-y-1" aria-label={copy.formOverviewTitle}>
                  {listingFormSections.map((section, index) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => jumpToFormSection(section.id)}
                        className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left text-sm font-medium text-muted-foreground transition hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-background text-xs font-bold text-brand-deep shadow-sm ring-1 ring-border/70 group-hover:bg-brand-soft">
                          {index + 1}
                        </span>
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="line-clamp-2">{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
                <div className="mt-5 flex items-start gap-2 rounded-xl bg-brand-soft/70 p-3 text-xs leading-relaxed text-brand-deep">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{copy.formPrivacyHint}</span>
                </div>
              </aside>

              <div
                ref={listingFormScrollRef}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-smooth bg-muted/25 px-4 py-4 sm:px-6 sm:py-6"
              >
                <nav
                  className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-2 lg:hidden"
                  aria-label={copy.formOverviewTitle}
                >
                  {listingFormSections.map((section, index) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => jumpToFormSection(section.id)}
                      className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="text-brand-deep">{index + 1}</span>
                      {section.label}
                    </button>
                  ))}
                </nav>

                {Object.keys(listingFormErrors).length > 0 && (
                  <div
                    className="mb-4 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 p-4 text-sm text-destructive"
                    role="alert"
                  >
                    <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                    <span className="font-medium">{copy.formErrorSummary}</span>
                  </div>
                )}

                <div className="mx-auto max-w-3xl space-y-5">
                  <section className="space-y-4 rounded-2xl border border-primary/20 bg-brand-soft/35 p-4 shadow-sm sm:p-6">
                    <div>
                      <h3 className="font-display text-xl font-bold">{packageCopy.title}</h3>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        {packageCopy.text}
                      </p>
                    </div>
                    <div
                      className="grid gap-3 sm:grid-cols-2"
                      role="radiogroup"
                      aria-label={packageCopy.title}
                    >
                      {propertyPlans.map((plan) => {
                        const selected = plan.code === listingPlanCode;
                        return (
                          <button
                            key={plan.code}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setListingPlanCode(plan.code as PropertyPlanCode)}
                            className={`rounded-2xl border bg-background p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              selected
                                ? "border-primary shadow-sm ring-1 ring-primary/20"
                                : "border-border hover:border-primary/40"
                            }`}
                          >
                            <span className="flex items-start justify-between gap-3">
                              <span>
                                <span className="block font-bold">
                                  {billingPlanName(plan.code, language)}
                                </span>
                                <span className="mt-1 block text-sm text-muted-foreground">
                                  {formatBillingPrice(plan.priceMinor, copy.locale, plan.currency)}{" "}
                                  · {packageCopy.oneTime}
                                </span>
                              </span>
                              {selected ? (
                                <Badge className="shrink-0">{packageCopy.selected}</Badge>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <DiscountCodeInput
                      planCode={listingPlanCode}
                      language={language}
                      onChange={setListingDiscount}
                      disabled={sendingListing}
                    />
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-offer"
                  >
                    <FormSectionHeading
                      id="form-section-offer"
                      number={1}
                      icon={Building2}
                      title={copy.formSectionOffer}
                      hint={copy.formSectionOfferHint}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="property-mode">
                          {copy.formMode} <RequiredMark />
                        </Label>
                        <select
                          id="property-mode"
                          name="mode"
                          required
                          className="h-12 w-full rounded-xl border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                        >
                          <option value="rent">{copy.mode.rent}</option>
                          <option value="buy">{copy.mode.buy}</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-kind">
                          {copy.formKind} <RequiredMark />
                        </Label>
                        <select
                          id="property-kind"
                          name="kind"
                          required
                          value={listingKind}
                          onChange={(event) => {
                            setListingKind(
                              event.target.value as (typeof PROPERTY_FORM_KINDS)[number],
                            );
                            setListingSubkind("");
                            setListingFormErrors((current) => {
                              if (!current.subkind) return current;
                              const next = { ...current };
                              delete next.subkind;
                              return next;
                            });
                          }}
                          className="h-12 w-full rounded-xl border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                        >
                          {PROPERTY_FORM_KINDS.map((option) => (
                            <option key={option} value={option}>
                              {copy.propertyTypes[option]}
                            </option>
                          ))}
                        </select>
                      </div>
                      {listingSubtypes.length > 0 ? (
                        <div className="space-y-2 sm:col-span-2">
                          <Label htmlFor="property-subkind">
                            {copy.formSubkind} <RequiredMark />
                          </Label>
                          <select
                            id="property-subkind"
                            name="subkind"
                            required
                            value={listingSubkind}
                            onChange={(event) => setListingSubkind(event.target.value)}
                            aria-invalid={Boolean(listingFormErrors.subkind)}
                            aria-describedby={
                              listingFormErrors.subkind ? "property-subkind-error" : undefined
                            }
                            className="h-12 w-full rounded-xl border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring/40 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                          >
                            <option value="" disabled>
                              {copy.formSubkindPlaceholder}
                            </option>
                            {listingSubtypes.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          <FormFieldMessage
                            id="property-subkind-error"
                            error={listingFormErrors.subkind}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property-title">
                        {copy.formObjectTitle} <RequiredMark />
                      </Label>
                      <Input
                        id="property-title"
                        name="title"
                        required
                        placeholder={copy.formTitlePlaceholder}
                        aria-invalid={Boolean(listingFormErrors.title)}
                        aria-describedby={
                          listingFormErrors.title ? "property-title-error" : "property-title-hint"
                        }
                        className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                      />
                      <FormFieldMessage
                        id={
                          listingFormErrors.title ? "property-title-error" : "property-title-hint"
                        }
                        error={listingFormErrors.title}
                      >
                        {copy.formTitleHint}
                      </FormFieldMessage>
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-location"
                  >
                    <FormSectionHeading
                      id="form-section-location"
                      number={2}
                      icon={MapPin}
                      title={copy.formSectionLocation}
                      hint={copy.formSectionLocationHint}
                    />
                    <div className="space-y-2">
                      <Label htmlFor="property-street">
                        {copy.formStreet} <RequiredMark />
                      </Label>
                      <Input
                        id="property-street"
                        name="street"
                        required
                        autoComplete="street-address"
                        placeholder={copy.formStreetPlaceholder}
                        aria-invalid={Boolean(listingFormErrors.street)}
                        aria-describedby={
                          listingFormErrors.street ? "property-street-error" : undefined
                        }
                        className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                      />
                      <FormFieldMessage
                        id="property-street-error"
                        error={listingFormErrors.street}
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-[140px_minmax(0,1fr)]">
                      <div className="space-y-2">
                        <Label htmlFor="property-postal-code">
                          {copy.formPostalCode} <RequiredMark />
                        </Label>
                        <Input
                          id="property-postal-code"
                          name="postalCode"
                          required
                          inputMode="numeric"
                          autoComplete="postal-code"
                          pattern="[0-9]{4}"
                          placeholder="4132"
                          aria-invalid={Boolean(listingFormErrors.postalCode)}
                          aria-describedby={
                            listingFormErrors.postalCode ? "property-postal-code-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-postal-code-error"
                          error={listingFormErrors.postalCode}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-location">
                          {copy.formLocation} <RequiredMark />
                        </Label>
                        <Input
                          id="property-location"
                          name="location"
                          required
                          autoComplete="address-level2"
                          placeholder={copy.formLocationPlaceholder}
                          aria-invalid={Boolean(listingFormErrors.location)}
                          aria-describedby={
                            listingFormErrors.location ? "property-location-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-location-error"
                          error={listingFormErrors.location}
                        />
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/70 bg-surface p-3.5">
                      <div className="flex items-start gap-3">
                        <Checkbox id="property-publish-address" name="publishAddress" />
                        <div>
                          <Label
                            htmlFor="property-publish-address"
                            className="cursor-pointer text-sm"
                          >
                            {copy.formPublishAddress}
                          </Label>
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {copy.formPublishAddressHint}
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-details"
                  >
                    <FormSectionHeading
                      id="form-section-details"
                      number={3}
                      icon={Ruler}
                      title={copy.formSectionDetails}
                      hint={copy.formSectionDetailsHint}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="property-rooms">
                          {copy.formRooms} <RequiredMark />
                        </Label>
                        <Input
                          id="property-rooms"
                          name="rooms"
                          required
                          min="1"
                          step="0.5"
                          type="number"
                          placeholder="3.5"
                          aria-invalid={Boolean(listingFormErrors.rooms)}
                          aria-describedby={
                            listingFormErrors.rooms ? "property-rooms-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-rooms-error"
                          error={listingFormErrors.rooms}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-area">
                          {copy.formArea} <RequiredMark />
                        </Label>
                        <Input
                          id="property-area"
                          name="area"
                          required
                          min="1"
                          type="number"
                          placeholder="92"
                          aria-invalid={Boolean(listingFormErrors.area)}
                          aria-describedby={
                            listingFormErrors.area ? "property-area-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage id="property-area-error" error={listingFormErrors.area} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-floor">{copy.formFloor}</Label>
                        <Input
                          id="property-floor"
                          name="floor"
                          placeholder={copy.formFloorPlaceholder}
                          className="h-12 rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-bathrooms">{copy.formBathrooms}</Label>
                        <Input
                          id="property-bathrooms"
                          name="bathrooms"
                          min="0.5"
                          step="0.5"
                          type="number"
                          placeholder="1"
                          aria-invalid={Boolean(listingFormErrors.bathrooms)}
                          aria-describedby={
                            listingFormErrors.bathrooms ? "property-bathrooms-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-bathrooms-error"
                          error={listingFormErrors.bathrooms}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-construction-year">
                          {copy.formConstructionYear}
                        </Label>
                        <Input
                          id="property-construction-year"
                          name="constructionYear"
                          min="1500"
                          max="2100"
                          type="number"
                          inputMode="numeric"
                          placeholder="2018"
                          aria-invalid={Boolean(listingFormErrors.constructionYear)}
                          aria-describedby={
                            listingFormErrors.constructionYear
                              ? "property-construction-year-error"
                              : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-construction-year-error"
                          error={listingFormErrors.constructionYear}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-condition">
                          {copy.formCondition} <RequiredMark />
                        </Label>
                        <select
                          id="property-condition"
                          name="condition"
                          defaultValue="good"
                          className="h-12 w-full rounded-xl border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                        >
                          {conditionOptions.map((option) => (
                            <option key={option} value={option}>
                              {copy.conditionLabels[option]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-price"
                  >
                    <FormSectionHeading
                      id="form-section-price"
                      number={4}
                      icon={CircleDollarSign}
                      title={copy.formSectionPrice}
                      hint={copy.formSectionPriceHint}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="property-price">
                          {copy.formPrice} <RequiredMark />
                        </Label>
                        <Input
                          id="property-price"
                          name="price"
                          required
                          min="1"
                          type="number"
                          inputMode="numeric"
                          placeholder="2450"
                          aria-invalid={Boolean(listingFormErrors.price)}
                          aria-describedby={
                            listingFormErrors.price ? "property-price-error" : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-price-error"
                          error={listingFormErrors.price}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-additional-costs">
                          {copy.formAdditionalCosts}
                        </Label>
                        <Input
                          id="property-additional-costs"
                          name="additionalCosts"
                          min="0"
                          type="number"
                          inputMode="numeric"
                          placeholder="250"
                          aria-invalid={Boolean(listingFormErrors.additionalCosts)}
                          aria-describedby={
                            listingFormErrors.additionalCosts
                              ? "property-additional-costs-error"
                              : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-additional-costs-error"
                          error={listingFormErrors.additionalCosts}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property-availability">
                        {copy.formAvailability} <RequiredMark />
                      </Label>
                      <Input
                        id="property-availability"
                        name="availability"
                        required
                        type="date"
                        aria-invalid={Boolean(listingFormErrors.availability)}
                        aria-describedby={
                          listingFormErrors.availability ? "property-availability-error" : undefined
                        }
                        className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                      />
                      <FormFieldMessage
                        id="property-availability-error"
                        error={listingFormErrors.availability}
                      />
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-presentation"
                  >
                    <FormSectionHeading
                      id="form-section-presentation"
                      number={5}
                      icon={FileText}
                      title={copy.formSectionPresentation}
                      hint={copy.formSectionPresentationHint}
                    />
                    <div className="space-y-2">
                      <Label htmlFor="property-description">
                        {copy.formDescriptionLabel} <RequiredMark />
                      </Label>
                      <Textarea
                        id="property-description"
                        name="description"
                        required
                        minLength={FORM_TEXT_MIN_LENGTH}
                        maxLength={FORM_TEXT_MAX_LENGTH}
                        rows={5}
                        placeholder={copy.formDescriptionPlaceholder}
                        onChange={(event) => setListingDescriptionLength(event.target.value.length)}
                        aria-invalid={Boolean(listingFormErrors.description)}
                        aria-describedby={`property-description-count${
                          listingFormErrors.description ? " property-description-error" : ""
                        }`}
                        className="min-h-36 resize-y rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                      />
                      <FormTextCounter
                        id="property-description-count"
                        length={listingDescriptionLength}
                        language={language}
                      />
                      {listingFormErrors.description && (
                        <FormFieldMessage
                          id="property-description-error"
                          error={listingFormErrors.description}
                        />
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>{copy.formAmenities}</Label>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {copy.formAmenitiesHint}
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {listingFormAmenityOptions.map((amenity) => {
                          const AmenityIcon = amenityIcons[amenity];
                          return (
                            <label
                              key={amenity}
                              htmlFor={`property-amenity-${amenity}`}
                              className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/70 bg-surface p-3.5 transition hover:border-brand/40 hover:bg-brand-soft/40"
                            >
                              <Checkbox
                                id={`property-amenity-${amenity}`}
                                name="amenities"
                                value={amenity}
                              />
                              <span className="pointer-events-none flex items-center gap-2 text-sm font-medium">
                                <AmenityIcon className="h-4 w-4 text-brand-deep" />
                                {copy.amenities[amenity]}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-media"
                  >
                    <FormSectionHeading
                      id="form-section-media"
                      number={6}
                      icon={Images}
                      title={copy.formSectionMedia}
                      hint={copy.formMediaHint}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2 rounded-2xl border border-dashed border-brand/35 bg-brand-soft/25 p-4">
                        <Label htmlFor="property-photos">
                          {copy.formPhotos} <RequiredMark />
                        </Label>
                        <Input
                          id="property-photos"
                          name="photos"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          required
                          aria-invalid={Boolean(listingFormErrors.photos)}
                          aria-describedby={
                            listingFormErrors.photos
                              ? "property-photos-error"
                              : "property-photos-hint"
                          }
                          onChange={(event) => {
                            const photoCount = event.currentTarget.files?.length ?? 0;
                            const exceedsLimit = photoCount > MAX_PROPERTY_PHOTOS;
                            setSelectedPhotoCount(photoCount);
                            setListingFormErrors((current) => {
                              const next = { ...current };
                              if (exceedsLimit) next.photos = copy.formPhotosLimitError;
                              else if (photoCount === 0) next.photos = copy.formPhotosRequiredError;
                              else delete next.photos;
                              return next;
                            });
                          }}
                          className="h-auto border-0 bg-transparent px-0 py-2 shadow-none file:mr-3 file:rounded-lg file:bg-brand-soft file:px-3 file:py-2 file:text-brand-deep aria-invalid:ring-0"
                        />
                        <FormFieldMessage
                          id={
                            listingFormErrors.photos
                              ? "property-photos-error"
                              : "property-photos-hint"
                          }
                          error={listingFormErrors.photos}
                        >
                          {selectedPhotoCount > 0
                            ? `${selectedPhotoCount} / ${MAX_PROPERTY_PHOTOS}`
                            : copy.formPhotosHint}
                        </FormFieldMessage>
                      </div>
                      <div className="space-y-2 rounded-2xl border border-dashed border-border bg-surface/60 p-4">
                        <Label htmlFor="property-floor-plan">{copy.formFloorPlan}</Label>
                        <Input
                          id="property-floor-plan"
                          name="floorPlan"
                          type="file"
                          accept="application/pdf,image/jpeg,image/png,image/webp"
                          className="h-auto border-0 bg-transparent px-0 py-2 shadow-none file:mr-3 file:rounded-lg file:bg-muted file:px-3 file:py-2 aria-invalid:ring-0"
                        />
                      </div>
                    </div>
                  </section>

                  <section
                    className="scroll-mt-4 space-y-5 rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-6"
                    aria-labelledby="form-section-contact"
                  >
                    <FormSectionHeading
                      id="form-section-contact"
                      number={7}
                      icon={Mail}
                      title={copy.formSectionContact}
                      hint={copy.formSectionContactHint}
                    />
                    <div className="flex items-start gap-2.5 rounded-xl bg-brand-soft/60 p-3 text-xs leading-relaxed text-brand-deep lg:hidden">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{copy.formPrivacyHint}</span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property-contact-name">
                        {copy.formContactName} <RequiredMark />
                      </Label>
                      <Input
                        id="property-contact-name"
                        name="contactName"
                        required
                        autoComplete="name"
                        aria-invalid={Boolean(listingFormErrors.contactName)}
                        aria-describedby={
                          listingFormErrors.contactName ? "property-contact-name-error" : undefined
                        }
                        className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                      />
                      <FormFieldMessage
                        id="property-contact-name-error"
                        error={listingFormErrors.contactName}
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="property-contact-email">
                          {copy.formContactEmail} <RequiredMark />
                        </Label>
                        <Input
                          id="property-contact-email"
                          name="contactEmail"
                          required
                          type="email"
                          autoComplete="email"
                          placeholder="name@beispiel.ch"
                          aria-invalid={Boolean(listingFormErrors.contactEmail)}
                          aria-describedby={
                            listingFormErrors.contactEmail
                              ? "property-contact-email-error"
                              : undefined
                          }
                          className="h-12 rounded-xl aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15"
                        />
                        <FormFieldMessage
                          id="property-contact-email-error"
                          error={listingFormErrors.contactEmail}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="property-contact-phone">{copy.formContactPhone}</Label>
                        <Input
                          id="property-contact-phone"
                          name="contactPhone"
                          type="tel"
                          autoComplete="tel"
                          placeholder="+41 79 123 45 67"
                          className="h-12 rounded-xl"
                        />
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 border-t border-border/70 bg-background px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="hidden max-w-sm text-xs leading-relaxed text-muted-foreground md:block">
                  {copy.formSubmitHint}
                </p>
                <div className="grid w-full grid-cols-2 gap-2 sm:ml-auto sm:flex sm:w-auto">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 rounded-xl px-5"
                    onClick={() => setListingDialogOpen(false)}
                  >
                    {copy.formCancel}
                  </Button>
                  <Button
                    type="submit"
                    disabled={sendingListing}
                    className="h-11 rounded-xl px-5 shadow-brand"
                  >
                    {sendingListing ? messages.sending : copy.formSubmit}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RequiredMark() {
  return (
    <span className="ml-0.5 text-destructive" aria-hidden="true">
      *
    </span>
  );
}

function FormSectionHeading({
  id,
  number,
  icon: Icon,
  title,
  hint,
}: {
  id: string;
  number: number;
  icon: typeof Building2;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex items-start gap-3 sm:gap-4">
      <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-deep">
        <Icon className="h-5 w-5" />
        <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-deep px-1 text-[10px] font-bold text-white ring-2 ring-background">
          {number}
        </span>
      </div>
      <div className="min-w-0 pt-0.5">
        <h3 id={id} className="font-display text-lg font-bold leading-tight sm:text-xl">
          {title}
        </h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

function FormFieldMessage({
  id,
  error,
  children,
}: {
  id: string;
  error?: string;
  children?: ReactNode;
}) {
  if (!error && !children) return null;

  return (
    <p
      id={id}
      className={`flex items-start gap-1.5 text-xs leading-relaxed ${
        error ? "font-medium text-destructive" : "text-muted-foreground"
      }`}
      role={error ? "alert" : undefined}
    >
      {error && <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" />}
      <span>{error || children}</span>
    </p>
  );
}
